/* eslint-disable no-unused-vars */
import React, { useRef, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setTokenStatus, deductTokens } from "../../store/slices/userSlice";
import { initGlobalSocket } from "../../services/socket";
import countryData from "../../components/Languages.json";
import UploadFileIcon from "../../assets/images/icon/uploadfile.png";
import DropdownIcon from "../../assets/images/icon/dropdown.png";
import AIIcon from "../../assets/images/icon/AI Icon.png";
import ExportIcon from "../../assets/images/icon/export.png";
import WarningIcon from "../../assets/images/icon/warning.png";
import useCustomScrollbar from "../../hooks/useCustomScrollbar";
import { useLanguage } from "../../context/LanguageContext";
import TranscribeService from "../../services/transcribe";
import { toastError, toastSuccess } from "../../components/Toast";
import MenuAIPanel from "../../components/common/MenuAIPanel";
import ContactUsPopup, { AlreadySentPopup } from "../../components/common/ContactUsPopup";
import { readStoredEmail } from "../../hooks/useSidebarData";
import PaymentService from "../../services/payment";
import HistoryService from "../../services/history";
import TrainingService from "../../services/training-group";
import { generatePdfFromHtml } from '../../utils/pdfFonts';
import { generateWordFromHtml } from '../../utils/wordExport';
import DOMPurify from "dompurify";
import { MD_PDF_STYLE } from "../../utils/markdownHtml";
import CopyIcon from "../../assets/images/icon/copy.png";
import { BadgeCheckIcon, Volume2, VolumeX, AlertCircle, Shield, Languages } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "") || "";
// HTTPS pages must use wss:// (browsers block insecure ws://); http pages use ws://
const SOCKET_BASE_URL =
  BASE_URL.replace(/^https/i, "wss").replace(/^http/i, "ws");
const LS_KEY = "linko_transcribe_result";
const saveToLS = (data) => { try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch (_e) { } };

const IDB_NAME = "linko_transcribe_db";
const IDB_STORE = "uploads";
const IDB_KEY = "uploaded_audio";

const openIdb = () =>
  new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) { reject(new Error("indexedDB unavailable")); return; }
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const idbStoreFile = async (file) => {
  try {
    const db = await openIdb();
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(file, IDB_KEY);
    return new Promise((resolve) => { tx.oncomplete = () => resolve(true); tx.onerror = () => resolve(false); });
  } catch (_e) { return false; }
};

const idbGetFile = async () => {
  try {
    const db = await openIdb();
    const tx = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
    return new Promise((resolve) => { req.onsuccess = () => resolve(req.result || null); req.onerror = () => resolve(null); });
  } catch (_e) { return null; }
};

const idbClearFile = async () => {
  try {
    const db = await openIdb();
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).delete(IDB_KEY);
    return new Promise((resolve) => { tx.oncomplete = () => resolve(true); tx.onerror = () => resolve(true); });
  } catch (_e) { return null; }
};

const mergeBuffers = (buffers) => {
  const totalLength = buffers.reduce((acc, buf) => acc + buf.length, 0);
  const result = new Float32Array(totalLength);
  let offset = 0;
  for (const buf of buffers) {
    result.set(buf, offset);
    offset += buf.length;
  }
  return result;
};

const encodeWAV = (samples, sampleRate) => {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (off, str) => { for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i)); };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    off += 2;
  }
  return new Blob([buffer], { type: "audio/wav" });
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || "");
      const idx = res.indexOf(",");
      resolve(idx !== -1 ? res.substring(idx + 1) : res);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
};

const audioBytes = (value) => {
  if (!value) return null;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  if (value?.type === "Buffer" && Array.isArray(value.data)) return new Uint8Array(value.data);
  if (Array.isArray(value)) return new Uint8Array(value);
  return null;
};

const generateSessionId = () => {
  return "ses_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 9);
};

const renderHighlightedText = (text) => {
  if (!text || typeof text !== "string") return text;
  if (!text.includes("<mark") && !text.includes("</mark>")) return text;

  // 1. Flatten consecutive/nested mark tags like <mark><mark>...</mark></mark>
  let clean = text
    .replace(/(<mark[^>]*>)\s*(<mark[^>]*>)+/gi, "$1")
    .replace(/(<\/mark>)\s*(<\/mark>)+/gi, "$1");

  const parts = [];
  const regex = /<mark[^>]*>([\s\S]*?)<\/mark>/gi;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(clean)) !== null) {
    if (match.index > lastIndex) {
      const plain = clean.slice(lastIndex, match.index).replace(/<\/?mark[^>]*>/gi, "");
      if (plain) parts.push(plain);
    }
    const inner = match[1].replace(/<\/?mark[^>]*>/gi, "");
    parts.push(
      <mark
        key={`transcribe-mark-${key++}`}
        className="bg-[#D8CEF6] text-[#3D2B90] font-semibold px-1.5 py-0.5 rounded-[4px] border-0 inline"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {inner}
      </mark>
    );
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < clean.length) {
    const plain = clean.slice(lastIndex).replace(/<\/?mark[^>]*>/gi, "");
    if (plain) parts.push(plain);
  }

  return parts;
};

const hasSpeech = (buffers, threshold = 0.01) => {
  if (!buffers.length) return false;
  const merged = mergeBuffers(buffers);
  let sum = 0;
  for (let i = 0; i < merged.length; i++) sum += merged[i] * merged[i];
  const rms = Math.sqrt(sum / merged.length);
  return rms > threshold;
};

const getTimestamp = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
};

const resolveAudioUrl = (url) => {
  if (!url) return "";
  const urlStr = typeof url === "string" ? url : (url?.url || url?.audioFileName || url?.audioUrl || url?.audioFile || "");
  if (typeof urlStr !== "string") return "";
  const trimmed = urlStr.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
    return trimmed;
  }

  const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const parsed = new URL(trimmed);
      const isInternal =
        parsed.hostname.startsWith("192.168.") ||
        parsed.hostname.startsWith("10.") ||
        parsed.hostname.startsWith("172.") ||
        parsed.hostname === "localhost" ||
        parsed.hostname === "127.0.0.1";
      if (isInternal && base) {
        const cleanPath = parsed.pathname.replace(/^\/+/, "");
        return `${base}/${cleanPath}${parsed.search}`;
      }
    } catch (_e) { }
    return trimmed;
  }

  const cleanPath = trimmed.replace(/^\/+/, "");
  if (base) {
    return `${base}/${cleanPath}`;
  }
  return trimmed;
};

const TranscribeTab = ({ onReset, onUndo, onUndoAvailable, onRedo, onRedoAvailable, audioHistory = [], latestAudioTranslation = null, onTranscriptionComplete, onHasContent, availableTokens, hasAiAccess = false, isActive = true, onSendToTranslate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduxTokenBalance = useSelector((state) => state.user?.tokenBalance);
  const reduxUser = useSelector((state) => state.user?.user);
  const isTokenExhausted =
    availableTokens === 0 ||
    reduxTokenBalance === 0 ||
    (typeof availableTokens === "number" && availableTokens <= 0) ||
    (typeof reduxTokenBalance === "number" && reduxTokenBalance <= 0);
  const { t } = useLanguage();
  const audioFileRef = useRef(null);
  const transcriptionRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const langDropdownRef = useRef(null);
  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const currentSegmentRecorderRef = useRef(null);
  const segmentTimerRef = useRef(null);
  const isPausedRef = useRef(false);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const pcmBuffersRef = useRef([]);
  const fullPcmBuffersRef = useRef([]);
  const chunkIntervalRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const audioStreamRef = useRef(null);
  const historyStackRef = useRef([]);
  const historyIndexRef = useRef(-1); // ADD THIS LINE - the missing declaration
  const audioRef = useRef(null);
  const sendChunkRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioDurationRef = useRef(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcribeProgress, setTranscribeProgress] = useState(() => {
    try {
      const p = localStorage.getItem("linko_pending_transcribe");
      if (p) {
        const d = JSON.parse(p);
        if (Date.now() - (d?.timestamp || 0) < 5 * 60 * 1000) {
          const prog = localStorage.getItem("linko_transcribe_progress");
          if (prog !== null && !isNaN(Number(prog))) {
            return Math.min(99, Math.max(0, Number(prog)));
          }
        }
      }
    } catch (_) {}
    return 0;
  });
  const [personalIdentifiersProtected, setPersonalIdentifiersProtected] = useState(null);
  const personalIdentifiersProtectedRef = useRef(null);
  const chunkItemRefs = useRef([]);
  const recordingDurationRef = useRef(0);

  const calculateBlobDuration = async (blobOrUrl) => {
    try {
      if (!blobOrUrl) return null;
      if (blobOrUrl instanceof Blob) {
        const tempUrl = URL.createObjectURL(blobOrUrl);
        const tempAudio = new Audio();
        tempAudio.src = tempUrl;
        tempAudio.onloadedmetadata = () => {
          if (isFinite(tempAudio.duration) && tempAudio.duration > 0) {
            setAudioDuration(tempAudio.duration);
            audioDurationRef.current = tempAudio.duration;
            URL.revokeObjectURL(tempUrl);
          }
        };
        tempAudio.onerror = () => {
          try { URL.revokeObjectURL(tempUrl); } catch (_e) { }
        };
        return null;
      } else if (typeof blobOrUrl === "string") {
        const resolved = resolveAudioUrl(blobOrUrl);
        if (!resolved) return null;
        const tempAudio = new Audio();
        tempAudio.src = resolved;
        tempAudio.onloadedmetadata = () => {
          if (isFinite(tempAudio.duration) && tempAudio.duration > 0) {
            setAudioDuration(tempAudio.duration);
            audioDurationRef.current = tempAudio.duration;
          }
        };
        return null;
      }
    } catch (_e) { }
    return null;
  };

  const getEffectiveDuration = () => {
    if (isFinite(audioDuration) && audioDuration > 0) return audioDuration;
    if (isFinite(audioDurationRef.current) && audioDurationRef.current > 0) return audioDurationRef.current;
    if (audioRef.current && isFinite(audioRef.current.duration) && audioRef.current.duration > 0) return audioRef.current.duration;
    if (isFinite(recordingDurationRef.current) && recordingDurationRef.current > 0) return recordingDurationRef.current;

    // Fallback to max timestamp in translation chunks
    if (translationChunks && translationChunks.length > 0) {
      let maxSec = 0;
      translationChunks.forEach((c, idx) => {
        const parsed = parseTimestampToSec(c.timestamp);
        const end = parsed?.end ?? (parsed ? parsed.start + 5 : (idx + 1) * 5);
        if (end > maxSec) maxSec = end;
      });
      if (maxSec > 0) return maxSec;
    }
    return 0;
  };

  const formatAudioTime = (sec) => {
    if (isNaN(sec) || !isFinite(sec) || sec <= 0) return "0mins 00 sec";
    const totalSec = Math.floor(sec);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const sStr = String(s).padStart(2, "0");
    if (h > 0) {
      return `${h}hs ${m}mins ${sStr} sec`;
    }
    return `${m}mins ${sStr} sec`;
  };

  const formatRecordingTime = (totalSeconds) => {
    const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const hrs = Math.floor(s / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    if (hrs > 0) {
      return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const [transcriptionText, setTranscriptionText] = useState("");
  const [selectedTranslation, setSelectedTranslation] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [langSearch, setLangSearch] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(() => {
    try {
      const p = localStorage.getItem("linko_pending_transcribe");
      if (p) {
        const d = JSON.parse(p);
        if (Date.now() - (d?.timestamp || 0) < 5 * 60 * 1000) {
          return true;
        }
      }
    } catch (_) {}
    return false;
  });
  const [showExportPicker, setShowExportPicker] = useState(false);
  const [recordingError, setRecordingError] = useState("");
  const [showLangMsg, setShowLangMsg] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [showAiTooltip, setShowAiTooltip] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [isContactUsSubmitting, setIsContactUsSubmitting] = useState(false);
  const [contactUsSuccess, setContactUsSuccess] = useState(false);
  const [showAlreadySentPopup, setShowAlreadySentPopup] = useState(false);

  const isContactRequestSent = useCallback(() => {
    if (hasAiAccess) return false;
    const note = localStorage.getItem("linko_rejection_note");
    if (note) return false;
    return (
      localStorage.getItem("linko_contact_sent") === "true" ||
      reduxUser?.is_contactus === true ||
      reduxUser?.is_contact_request === true ||
      reduxUser?.contact_request === true ||
      reduxUser?.is_request === true
    );
  }, [hasAiAccess, reduxUser]);
  const [showNewTranscribeConfirm, setShowNewTranscribeConfirm] = useState(false);
  const [isCurrentTranscriptionSaved, setIsCurrentTranscriptionSaved] = useState(false);
  const [presets, setPresets] = useState([]);
  const [isRetranslating, setIsRetranslating] = useState(false);
  const confirmActionRef = useRef("upload");

  const handleContactUsSubmit = async (form) => {
    try {
      setIsContactUsSubmitting(true);
      const payload = {
        email: form.email,
        name: form.name,
        phone_no: form.phone_no,
        country_code: form.country_code,
        note: form.note,
      };
      await PaymentService.contactUsPlan(payload);
      setShowContactUs(false);
      setContactUsSuccess(true);
      try {
        localStorage.setItem("linko_contact_sent", "true");
        localStorage.removeItem("linko_rejection_note");
      } catch (_) {}
      window.dispatchEvent(new CustomEvent("contactStatusChanged", { detail: { sent: true } }));
      window.dispatchEvent(new Event("planUpdated"));
      window.dispatchEvent(new Event("profileUpdated"));
    } catch (error) {
      toastError(
        error?.response?.data?.message ||
        "Failed to send request. Please try again."
      );
    } finally {
      setIsContactUsSubmitting(false);
    }
  };

  const selectedLanguageRef = useRef("");
  const transcriptionTextRef = useRef("");
  const currentRecordingIdRef = useRef(null);
  const savedTranscriptionRef = useRef("");
  const recordingTimerRef = useRef(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isTextDirty, setIsTextDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [responseTime, setResponseTime] = useState(null);
  const [translationChunks, setTranslationChunks] = useState([]);
  const [lastTimestamp, setLastTimestamp] = useState("");
  const savedTranslationRef = useRef("");
  const isUserEditingRef = useRef(false);
  const isNearBottomRef = useRef(true);
  const currentChunksRef = useRef([]);
  const currentResponseTimeRef = useRef(null);
  const [emptyTranscriptionError, setEmptyTranscriptionError] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const lastTimestampRef = useRef("");
  const [socketConnected, setSocketConnected] = useState(false);
  const [socketProcessing, setSocketProcessing] = useState(false);
  const [awaitingFinal, setAwaitingFinal] = useState(false);
  const sessionIdRef = useRef(null);
  const isRecordingActiveRef = useRef(false);
  const isUploadActiveRef = useRef(false);
  const mountedRef = useRef(false);
  const audioUrlRef = useRef("");
  const finalResultWaitTimerRef = useRef(null);
  const lastResultAtRef = useRef(0);
  // Prevent the audio onError handler from creating a loop of fallback attempts
  const audioErrorFallbackRef = useRef(false);
  // Stable ref for onTranscriptionComplete to avoid useEffect deps churn
  const onTranscriptionCompleteRef = useRef(null);

  const syncAudioUrl = (url) => {
    const resolved = resolveAudioUrl(url);
    if (resolved === audioUrlRef.current) return; // no-op if unchanged
    audioUrlRef.current = resolved;
    setAudioUrl(resolved);
  };

  // Call this when explicitly loading a brand-new audio source (upload, recording, server final result)
  // This resets the error fallback guard so the new source is allowed one IDB fallback attempt
  const setNewAudioUrl = (url) => {
    audioErrorFallbackRef.current = false;
    syncAudioUrl(url);
  };

  // Keep the callback ref in sync with the latest prop so effects don't need it as dependency
  onTranscriptionCompleteRef.current = onTranscriptionComplete;

  const handleReset = (keepLanguage = false) => {
    try { localStorage.removeItem(LS_KEY); } catch (_e) { }
    idbClearFile().catch(() => { });
    setTranscriptionText("");
    setSelectedTranslation("");
    setTranslationChunks([]);
    setLastTimestamp("");
    setWordCount(0);
    if (!keepLanguage) {
      setSelectedLanguage("");
      selectedLanguageRef.current = "";
    }
    setRecordingError("");
    setShowLangMsg(false);
    setEmptyTranscriptionError(false);
    setIsRecording(false);
    setIsPaused(false);
    sendChunkRef.current = null;
    syncAudioUrl("");
    setUploadedFile(null);
    setIsSending(false);
    setIsTranscribing(false);
    setTranscribeProgress(0);
    setPersonalIdentifiersProtected(null);
    personalIdentifiersProtectedRef.current = null;
    setIsTextDirty(false);
    setIsCurrentTranscriptionSaved(false);
    setIsSaving(false);
    setIsRetranslating(false);
    setSocketProcessing(false);
    setAwaitingFinal(false);
    transcriptionTextRef.current = "";
    savedTranscriptionRef.current = "";
    savedTranslationRef.current = "";
    historyStackRef.current = [];
    historyIndexRef.current = -1;
    setCanUndo(false);
    setCanRedo(false);
    lastTimestampRef.current = "";
    currentRecordingIdRef.current = null;
    currentChunksRef.current = [];
    currentResponseTimeRef.current = null;
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    setRecordingSeconds(0);
    if (chunkIntervalRef.current) { clearInterval(chunkIntervalRef.current); chunkIntervalRef.current = null; }
    if (audioProcessorRef.current) { audioProcessorRef.current.disconnect(); audioProcessorRef.current = null; }
    if (audioSourceRef.current) { audioSourceRef.current.disconnect(); audioSourceRef.current = null; }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") { audioContextRef.current.close(); audioContextRef.current = null; }
    if (audioStreamRef.current) { audioStreamRef.current.getTracks().forEach(t => t.stop()); audioStreamRef.current = null; }
    pcmBuffersRef.current = [];
    fullPcmBuffersRef.current = [];
    chunkIndexRef.current = 0;
    if (segmentTimerRef.current) { clearTimeout(segmentTimerRef.current); segmentTimerRef.current = null; }
    if (currentSegmentRecorderRef.current && currentSegmentRecorderRef.current.state !== "inactive") {
      try { currentSegmentRecorderRef.current.stop(); } catch (_e) { }
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (_e) { }
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current.currentTime = 0;
      } catch (_e) { }
    }
    setCurrentTime(0);
    setAudioDuration(0);
    audioDurationRef.current = 0;
    setIsPlaying(false);
    setIsMuted(false);
    try {
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem("linko_pending_transcribe");
      localStorage.removeItem("linko_transcribe_progress");
    } catch (_e) { }
    idbClearFile().catch(() => { });
  };

  const cloneChunks = (chunks) => JSON.parse(JSON.stringify(chunks || []));

  const captureState = () => ({
    transcription: transcriptionTextRef.current,
    translation: savedTranslationRef.current,
    chunks: cloneChunks(currentChunksRef.current),
    responseTime: currentResponseTimeRef.current,
    ts: (currentChunksRef.current?.length
      ? currentChunksRef.current[currentChunksRef.current.length - 1].timestamp
      : lastTimestampRef.current) || "",
  });

  const persistAll = (currentAudioUrl) => {
    const urlToSave = currentAudioUrl || audioUrlRef.current || audioUrl;
    audioUrlRef.current = urlToSave;
    saveToLS({
      transcription: transcriptionTextRef.current,
      translation: savedTranslationRef.current,
      id: currentRecordingIdRef.current,
      language: selectedLanguageRef.current,
      rt: currentResponseTimeRef.current,
      chunks: currentChunksRef.current,
      ts: lastTimestampRef.current,
      historyStack: historyStackRef.current,
      historyIndex: historyIndexRef.current,
      audioUrl: urlToSave,
      duration: audioDurationRef.current || audioDuration,
      personalIdentifiersProtected: personalIdentifiersProtectedRef.current,
    });
  };

  useEffect(() => {
    if (mountedRef.current) persistAll(audioUrl);
    if (audioRef.current && audioUrl) {
      if (audioRef.current.src !== audioUrl) {
        audioRef.current.src = audioUrl;
      }
      try {
        audioRef.current.load();
      } catch (_e) { }
    }
  }, [audioUrl]);

  useEffect(() => {
    if (mountedRef.current && typeof personalIdentifiersProtected === "number") {
      personalIdentifiersProtectedRef.current = personalIdentifiersProtected;
      persistAll();
    }
  }, [personalIdentifiersProtected]);

  useEffect(() => {
    const handleProgress = (e) => {
      const p = Number(e.detail);
      if (!isNaN(p)) {
        if (p >= 100) {
          setTranscribeProgress(100);
        } else {
          setTranscribeProgress(Math.min(99, Math.max(0, p)));
        }
      }
    };

    const handleTranscriptionCompleted = (e) => {
      const data = e.detail;
      if (!data) return;
      const d = data?.data || data?.latestAudioTranslation || data;
      const transcription = d?.transcription || d?.originalText || d?.extractedText || data?.transcription || data?.originalText || "";
      const translation = d?.translation || d?.translatedText || data?.translation || data?.translatedText || "";

      let segments = [];
      if (Array.isArray(d?.segments) && d.segments.length > 0) {
        segments = d.segments;
      } else if (Array.isArray(d?.chunks)) {
        d.chunks.forEach((chunk) => {
          if (Array.isArray(chunk?.segments)) {
            segments.push(...chunk.segments);
          }
        });
      } else if (Array.isArray(data?.segments) && data.segments.length > 0) {
        segments = data.segments;
      }

      let chunks = [];
      if (segments.length > 0) {
        chunks = segments.map((s, idx) => ({
          timestamp: s.timestamp || (s.start !== undefined && s.end !== undefined ? `${Math.round(s.start)}-${Math.round(s.end)}s` : `${idx * 5}-${(idx + 1) * 5}s`),
          translatedText: s.translatedText || s.translation || s.text || "",
          person: s.person || s.speaker || `Person 1`,
          originalText: s.originalText || s.transcription || s.text || "",
          start: s.start,
          end: s.end,
        }));
      }

      const id = d?.id || d?._id || data?.id || data?._id || null;
      const lang = d?.targetLanguage || data?.targetLanguage || selectedLanguageRef.current;
      const pi = d?.personalIdentifiersProtected ?? (typeof data?.personalIdentifiersProtected === "number" ? data.personalIdentifiersProtected : null);
      const audioSrc = d?.audioFileName || d?.audioUrl || d?.audioFile || data?.audioFileName || data?.audioUrl || null;

      if (id) currentRecordingIdRef.current = id;
      if (typeof pi === "number") {
        setPersonalIdentifiersProtected(pi);
        personalIdentifiersProtectedRef.current = pi;
      }
      if (lang) {
        setSelectedLanguage(lang);
        selectedLanguageRef.current = lang;
      }
      if (audioSrc) {
        setNewAudioUrl(audioSrc);
      }

      transcriptionTextRef.current = transcription;
      savedTranscriptionRef.current = transcription;
      savedTranslationRef.current = translation;
      currentChunksRef.current = chunks;
      lastTimestampRef.current = chunks.length > 0 ? chunks[chunks.length - 1].timestamp : "";

      setTranscriptionText(transcription);
      setSelectedTranslation(translation);
      setTranslationChunks(chunks);
      if (chunks.length > 0) {
        setLastTimestamp(chunks[chunks.length - 1].timestamp);
      }
      setWordCount((translation || transcription).trim() === "" ? 0 : (translation || transcription).trim().split(/\s+/).length);
      setIsCurrentTranscriptionSaved(true);
      setTranscribeProgress(100);
      setIsTranscribing(false);

      try {
        localStorage.removeItem("linko_pending_transcribe");
        localStorage.removeItem("linko_transcribe_progress");
      } catch (_) {}

      persistAll(audioSrc);
    };

    const handleTranscriptionError = (e) => {
      const data = e.detail;
      const errMsg = data?.error || data?.message || data?.data?.message || (typeof data === "string" ? data : "Audio transcription failed");
      setRecordingError(errMsg);
      toastError(errMsg);

      const partial = data?.partialResult || {};
      let chunks = [];
      if (Array.isArray(partial.segments) && partial.segments.length > 0) {
        chunks = partial.segments.map((s, idx) => ({
          timestamp: s.timestamp || (s.start !== undefined && s.end !== undefined ? `${Math.round(s.start)}-${Math.round(s.end)}s` : `${idx * 5}-${(idx + 1) * 5}s`),
          translatedText: s.translatedText || s.translation || s.text || "",
          person: s.person || s.speaker || `Person 1`,
          originalText: s.originalText || s.transcription || s.text || "",
          start: s.start,
          end: s.end,
        }));
      }

      const text = partial.transcription || (chunks.length > 0 ? chunks.map((c) => c.originalText).filter(Boolean).join(" ") : "");
      const translation = partial.translation || partial.translatedText || (chunks.length > 0 ? chunks.map((c) => c.translatedText).filter(Boolean).join(" ") : "");

      if (text || translation || chunks.length > 0) {
        setEmptyTranscriptionError(false);
        transcriptionTextRef.current = text;
        savedTranscriptionRef.current = text;
        savedTranslationRef.current = translation;
        currentChunksRef.current = chunks;
        setTranscriptionText(text);
        setSelectedTranslation(translation);
        setTranslationChunks(chunks);
        if (chunks.length > 0) {
          const lastTs = chunks[chunks.length - 1]?.timestamp || "";
          lastTimestampRef.current = lastTs;
          setLastTimestamp(lastTs);
        }
        setWordCount((translation || text).trim() === "" ? 0 : (translation || text).trim().split(/\s+/).length);
        setIsCurrentTranscriptionSaved(true);
        persistAll(partial.audioFileName || audioUrl);
      }

      setIsTranscribing(false);
      setTranscribeProgress(0);
      try {
        localStorage.removeItem("linko_pending_transcribe");
        localStorage.removeItem("linko_transcribe_progress");
      } catch (_) {}
    };

    window.addEventListener("transcribeProgressUpdated", handleProgress);
    window.addEventListener("transcriptionCompleted", handleTranscriptionCompleted);
    window.addEventListener("transcriptionError", handleTranscriptionError);
    return () => {
      window.removeEventListener("transcribeProgressUpdated", handleProgress);
      window.removeEventListener("transcriptionCompleted", handleTranscriptionCompleted);
      window.removeEventListener("transcriptionError", handleTranscriptionError);
    };
  }, []);

  const statesEqual = (a, b) => {
    if (!a || !b) return false;
    return a.transcription === b.transcription
      && a.translation === b.translation
      && JSON.stringify(a.chunks || []) === JSON.stringify(b.chunks || []);
  };

  const applyState = (state) => {
    if (!state) return;
    transcriptionTextRef.current = state.transcription || "";
    savedTranscriptionRef.current = state.transcription || "";
    savedTranslationRef.current = state.translation || "";
    currentChunksRef.current = cloneChunks(state.chunks);
    currentResponseTimeRef.current = state.responseTime ?? null;
    lastTimestampRef.current = state.ts || "";
    setTranscriptionText(state.transcription || "");
    setSelectedTranslation(state.translation || "");
    setTranslationChunks(cloneChunks(state.chunks));
    setLastTimestamp(state.ts || "");
    setResponseTime(state.responseTime ?? null);
    const trans = state.translation || "";
    setWordCount(trans.trim() === "" ? 0 : trans.trim().split(/\s+/).length);
    setIsTextDirty(state.saved === false);
    persistAll();
  };

  const syncUndoRedo = () => {
    const current = captureState();
    const committed = historyStackRef.current[historyIndexRef.current];
    const hasUnsaved = committed && !statesEqual(current, committed);
    setCanUndo(historyIndexRef.current > 0 || !!hasUnsaved);
    setCanRedo(historyIndexRef.current < historyStackRef.current.length - 1);
  };

  const resetHistoryToCurrent = () => {
    historyStackRef.current = [{ ...captureState(), saved: true }];
    historyIndexRef.current = 0;
    persistAll();
    syncUndoRedo();
  };

  const commitCurrentToHistory = (saved = true) => {
    const current = { ...captureState(), saved };
    const committed = historyStackRef.current[historyIndexRef.current];
    if (committed && statesEqual(current, committed)) {
      if (committed) committed.saved = saved;
      persistAll();
      syncUndoRedo();
      return;
    }
    historyStackRef.current = historyStackRef.current.slice(0, historyIndexRef.current + 1);
    historyStackRef.current.push(current);
    historyIndexRef.current = historyStackRef.current.length - 1;
    persistAll();
    syncUndoRedo();
  };

  const handleUndo = () => {
    const current = captureState();
    const committed = historyStackRef.current[historyIndexRef.current];
    const hasUnsaved = committed && !statesEqual(current, committed);

    if (hasUnsaved) {
      historyStackRef.current = historyStackRef.current.slice(0, historyIndexRef.current + 1);
      historyStackRef.current.push({ ...current, saved: false });
      applyState(historyStackRef.current[historyIndexRef.current]);
      syncUndoRedo();
      return;
    }

    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    applyState(historyStackRef.current[historyIndexRef.current]);
    syncUndoRedo();
  };

  const handleRedo = () => {
    if (historyIndexRef.current >= historyStackRef.current.length - 1) return;
    historyIndexRef.current += 1;
    applyState(historyStackRef.current[historyIndexRef.current]);
    syncUndoRedo();
  };

  const copyToClipboard = (text, onSuccess) => {
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(() => {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try { document.execCommand("copy"); onSuccess(); } catch { }
        document.body.removeChild(ta);
      });
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand("copy"); onSuccess(); } catch { }
      document.body.removeChild(ta);
    }
  };

  const handleCopyAll = () => {
    const chunks = currentChunksRef.current;
    let text = "";
    if (chunks.length > 0) {
      const paragraphs = groupChunksIntoParagraphs(chunks);
      text = paragraphs
        .map((para) =>
          para
            .map(
              (c) =>
                `${formatInlineTimestamp(c.timestamp, c.globalIndex)} ${c.translatedText}`
            )
            .join(" ")
        )
        .join("\n\n");
    } else {
      text = [transcriptionTextRef.current, savedTranslationRef.current].filter(Boolean).join("\n\n");
    }
    copyToClipboard(text, () => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
    });
  };

  const connectSocket = (force = false) => {
    const token = localStorage.getItem("linko_web");
    if (!token) return Promise.reject(new Error("No auth token"));

    const sock = initGlobalSocket(dispatch);
    if (!sock) return Promise.reject(new Error("Socket unavailable"));
    socketRef.current = sock;

    // Attach transcribe-specific listeners
    sock.off("connect_error");
    sock.on("connect_error", (err) => {
      setSocketConnected(false);
      setAwaitingFinal(false);
      setIsTranscribing(false);
      setIsSending(false);
      setSocketProcessing(false);
      setRecordingError(t.socketConnectionFailed + ": " + err.message);
    });

    sock.off("disconnect");
    sock.on("disconnect", () => {
      setSocketConnected(false);
      setAwaitingFinal(false);
      setIsTranscribing(false);
      setIsSending(false);
      setSocketProcessing(false);
    });

    sock.off("error");
    sock.on("error", (data) => {
      setAwaitingFinal(false);
      setIsTranscribing(false);
      setIsSending(false);
      setSocketProcessing(false);
      const errMsg = data?.message || data?.error || (typeof data === "string" ? data : "Please purchase a plan to access this feature.");
      setRecordingError(errMsg);
      toastError(errMsg);
    });

    // Handle translation result from socket - each chunk result shows in UI
    sock.off("translation-result");
    sock.on("translation-result", (data) => {
      const sid = data.sessionId || data.session_id;
      if (sid && sid !== sessionIdRef.current) return;

      const originalText = data.originalText || "";
      const translatedText = data.translatedText || "";

      if (translatedText) {
        const isRecord = !!sessionIdRef.current;
        const currentChunkIdx = currentChunksRef.current.length;
        let chunkTs = data.timestamp;
        if (!chunkTs) {
          if (data.startTime !== undefined && data.endTime !== undefined) {
            chunkTs = `${data.startTime}-${data.endTime}s`;
          } else {
            chunkTs = `${currentChunkIdx * 30}-${(currentChunkIdx + 1) * 30}s`;
          }
        }
        const chunk = {
          timestamp: chunkTs,
          translatedText: translatedText,
          person: data.person || data.speaker || (isRecord ? "Speaker 1" : "Speaker"),
          originalText: originalText,
          source: isRecord ? "record" : "upload",
          start: data.startTime ?? data.start,
          end: data.endTime ?? data.end,
        };

        const piVal = data?.personalIdentifiersProtected ?? data?.personal_identifiers_protected ?? data?.data?.personalIdentifiersProtected ?? null;
        if (piVal !== null && !isNaN(Number(piVal))) {
          const num = Number(piVal);
          setPersonalIdentifiersProtected(num);
          personalIdentifiersProtectedRef.current = num;
        }
        const updatedChunks = [...currentChunksRef.current, chunk];
        currentChunksRef.current = updatedChunks;
        setTranslationChunks(updatedChunks);

        const allTranslations = updatedChunks.map((c) => c.translatedText).join("\n\n");
        savedTranslationRef.current = allTranslations;
        setSelectedTranslation(allTranslations);
        setWordCount(allTranslations.trim() === "" ? 0 : allTranslations.trim().split(/\s+/).length);
        setIsTextDirty(false);
        setIsCurrentTranscriptionSaved(false);

        const allOriginals = updatedChunks.map((c) => c.originalText).filter(Boolean).join("\n\n");
        if (allOriginals) {
          transcriptionTextRef.current = allOriginals;
          savedTranscriptionRef.current = allOriginals;
          setTranscriptionText(allOriginals);
        }

        // Only set audio URL for the FIRST chunk (if none is set yet).
        // Changing audio src on every chunk floods the browser with media requests.
        if (!isRecord && !audioUrlRef.current) {
          const bytes = audioBytes(data.translatedAudio);
          if (bytes) {
            const transAudioUrl = URL.createObjectURL(new Blob([bytes], { type: "audio/mpeg" }));
            setNewAudioUrl(transAudioUrl);
            calculateBlobDuration(transAudioUrl);
          } else if (data.audioUrl || data.audioFileName) {
            setNewAudioUrl(data.audioUrl || data.audioFileName);
            calculateBlobDuration(data.audioUrl || data.audioFileName);
          }
        }

        lastTimestampRef.current = chunk.timestamp;
        setLastTimestamp(chunk.timestamp);
        lastResultAtRef.current = Date.now();
        if (finalResultWaitTimerRef.current) {
          clearTimeout(finalResultWaitTimerRef.current);
          finalResultWaitTimerRef.current = null;
        }
        finalResultWaitTimerRef.current = setTimeout(() => {
          if (Date.now() - lastResultAtRef.current >= 1500) {
            setAwaitingFinal(false);
            setSocketProcessing(false);
            finalResultWaitTimerRef.current = null;
          }
        }, 1500);
        // Deduct tokens from Redux in real time based on chunk usage
        const chunkTokens = Number(data?.totalToken ?? (Number(data?.inputToken || 0) + Number(data?.outputToken || 0))) || 0;
        if (chunkTokens > 0 && dispatch) {
          dispatch(deductTokens(chunkTokens));
        }
        if (data?.available_tokens !== undefined || data?.availableTokens !== undefined) {
          dispatch(setTokenStatus(data));
        }

        persistAll();
      }
    });

    sock.off("translation-error");
    sock.on("translation-error", (data) => {
      const sid = data?.sessionId || data?.session_id;
      if (sid && sessionIdRef.current && sid !== sessionIdRef.current) return;
      if (finalResultWaitTimerRef.current) {
        clearTimeout(finalResultWaitTimerRef.current);
        finalResultWaitTimerRef.current = null;
      }
      setAwaitingFinal(false);
      setIsTranscribing(false);
      setIsSending(false);
      setSocketProcessing(false);
      toastError(data?.message || t.translationFailed);
    });

    if (sock.connected) {
      setSocketConnected(true);
      return Promise.resolve(sock);
    }

    return new Promise((resolve) => {
      sock.once("connect", () => {
        setSocketConnected(true);
        setRecordingError("");
        resolve(sock);
      });
    });
  };

  useEffect(() => {
    return () => {
      if (finalResultWaitTimerRef.current) {
        clearTimeout(finalResultWaitTimerRef.current);
        finalResultWaitTimerRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.off("translation-result");
        socketRef.current.off("translation-error");
        socketRef.current = null;
      }
      setSocketConnected(false);
    };
  }, []);

  useEffect(() => {
    if (onReset) onReset(handleReset);
    if (onUndo) onUndo(handleUndo);
    if (onRedo) onRedo(handleRedo);

    try {
      const pendingStr = localStorage.getItem("linko_pending_transcribe");
      let isPending = false;
      if (pendingStr) {
        try {
          const pd = JSON.parse(pendingStr);
          if (Date.now() - (pd?.timestamp || 0) < 15 * 60 * 1000) {
            isPending = true;
          }
        } catch (_) {}
      }

      const saved = localStorage.getItem(LS_KEY);
      if (saved && !isPending) {
        const parsed = JSON.parse(saved);
        const { transcription, translation, id, language, rt, chunks, ts, historyStack, historyIndex, audioUrl: savedAudioUrl } = parsed;
        if (savedAudioUrl && !savedAudioUrl.startsWith("blob:") && !savedAudioUrl.startsWith("data:")) {
          syncAudioUrl(savedAudioUrl);
        }
        if (parsed.duration && isFinite(parsed.duration) && parsed.duration > 0) {
          setAudioDuration(parsed.duration);
          audioDurationRef.current = parsed.duration;
        }
        if (typeof parsed.personalIdentifiersProtected === "number") {
          setPersonalIdentifiersProtected(parsed.personalIdentifiersProtected);
          personalIdentifiersProtectedRef.current = parsed.personalIdentifiersProtected;
        }
        if (translation || (Array.isArray(chunks) && chunks.length > 0)) {
          const restoredChunks = Array.isArray(chunks) && chunks.length > 0 ? chunks : [];
          transcriptionTextRef.current = transcription || "";
          savedTranscriptionRef.current = transcription || "";
          savedTranslationRef.current = translation || "";
          currentChunksRef.current = restoredChunks;
          currentResponseTimeRef.current = rt || null;
          lastTimestampRef.current = ts || "";
          if (id) currentRecordingIdRef.current = id;
          if (language) { setSelectedLanguage(language); selectedLanguageRef.current = language; }
          setTranscriptionText(transcription || "");
          setSelectedTranslation(translation || "");
          setTranslationChunks(restoredChunks);
          if (ts) setLastTimestamp(ts);
          const totalWords = (translation || "").trim() === "" ? 0 : (translation || "").trim().split(/\s+/).length;
          setWordCount(totalWords);
          if (rt) setResponseTime(rt);
          setIsCurrentTranscriptionSaved(true);
          if (Array.isArray(historyStack) && historyStack.length > 0) {
            historyStackRef.current = historyStack;
            historyIndexRef.current = typeof historyIndex === "number" ? historyIndex : historyStack.length - 1;
          } else {
            historyStackRef.current = [captureState()];
            historyIndexRef.current = 0;
          }
          syncUndoRedo();
        }
      }
    } catch (_e) { }

    idbGetFile().then((file) => {
      if (file && (file instanceof Blob || file.type)) {
        setUploadedFile(file);
        const u = URL.createObjectURL(file);
        setNewAudioUrl(u);
        calculateBlobDuration(file);
      }
    }).catch(() => { });

    const resumePendingTranscribe = () => {
      try {
        const p = localStorage.getItem("linko_pending_transcribe");
        if (!p) return;
        const d = JSON.parse(p);
        if (Date.now() - (d?.timestamp || 0) > 5 * 60 * 1000) {
          localStorage.removeItem("linko_pending_transcribe");
          localStorage.removeItem("linko_transcribe_progress");
          setIsTranscribing(false);
          return;
        }
        setIsTranscribing(true);
      } catch (_) {
      }
    };

    resumePendingTranscribe();

    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Report undo availability so the parent can enable/disable the Undo button
  useEffect(() => {
    if (onUndoAvailable) onUndoAvailable(canUndo);
  }, [canUndo, onUndoAvailable]);

  useEffect(() => {
    if (onRedoAvailable) onRedoAvailable(canRedo);
  }, [canRedo, onRedoAvailable]);

  // Helper to parse timestamp strings like "0-5s", "00:05", "01:23" into seconds
  const parseTimestampToSec = (ts) => {
    if (!ts || typeof ts !== "string") return null;
    const rangeMatch = ts.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)s?$/);
    if (rangeMatch) {
      return { start: parseFloat(rangeMatch[1]), end: parseFloat(rangeMatch[2]) };
    }
    const clean = ts.replace(/[\[\]\(\)]/g, "").trim();
    if (clean.includes("–") || clean.includes("-")) {
      const parts = clean.split(/[–\-]/).map((p) => p.trim());
      if (parts.length === 2) {
        const s = parseTimestampToSec(parts[0]);
        const e = parseTimestampToSec(parts[1]);
        if (s) return { start: s.start, end: e ? e.start : s.start + 5 };
      }
    }
    const parts = clean.split(":").map(Number);
    if (parts.some(isNaN)) return null;
    if (parts.length === 3) {
      return { start: parts[0] * 3600 + parts[1] * 60 + parts[2], end: null };
    }
    if (parts.length === 2) {
      return { start: parts[0] * 60 + parts[1], end: null };
    }
    return null;
  };

  // Helper to format timestamp into clean inline (M:SS) or (H:MM:SS) format like Image 2
  const formatInlineTimestamp = (ts, index = 0) => {
    if (!ts || typeof ts !== "string") {
      const totalSec = index * 5;
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `(${m}:${String(s).padStart(2, "0")})`;
    }
    const rangeMatch = ts.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)s?$/);
    if (rangeMatch) {
      const totalSec = Math.floor(parseFloat(rangeMatch[1]));
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      if (h > 0) return `(${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")})`;
      return `(${m}:${String(s).padStart(2, "0")})`;
    }
    const clean = ts.replace(/[\[\]\(\)]/g, "").trim();
    if (clean.includes("–") || clean.includes("-")) {
      const parts = clean.split(/[–\-]/).map((p) => p.trim());
      if (parts.length >= 1) {
        const s = parseTimestampToSec(parts[0]);
        if (s) {
          const totalSec = Math.floor(s.start);
          const h = Math.floor(totalSec / 3600);
          const m = Math.floor((totalSec % 3600) / 60);
          const sec = totalSec % 60;
          if (h > 0) return `(${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")})`;
          return `(${m}:${String(sec).padStart(2, "0")})`;
        }
      }
    }
    const parts = clean.split(":").map(Number);
    if (!parts.some(isNaN)) {
      if (parts.length === 3) {
        const h = parts[0];
        const m = parts[1];
        const s = parts[2];
        if (h > 0) return `(${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")})`;
        return `(${m}:${String(s).padStart(2, "0")})`;
      }
      if (parts.length === 2) {
        return `(${parts[0]}:${String(parts[1]).padStart(2, "0")})`;
      }
    }
    return `(${clean})`;
  };

  const groupChunksIntoParagraphs = (chunks) => {
    if (!chunks || chunks.length === 0) return [];
    const paragraphs = [];
    let currentPara = [];
    let currentPerson = null;

    chunks.forEach((chunk, index) => {
      const chunkPerson = chunk.person || chunk.speaker || "";
      const isPersonChange = currentPara.length > 0 && chunkPerson && currentPerson && chunkPerson !== currentPerson;

      if (isPersonChange) {
        paragraphs.push(currentPara);
        currentPara = [];
      }

      currentPara.push({ ...chunk, globalIndex: index });
      currentPerson = chunkPerson;

      if (chunk.translatedText?.includes("\n\n")) {
        paragraphs.push(currentPara);
        currentPara = [];
        currentPerson = null;
      }
    });

    if (currentPara.length > 0) {
      paragraphs.push(currentPara);
    }
    return paragraphs;
  };

  const getActiveChunkIndex = () => {
    if (!isPlaying || !translationChunks || translationChunks.length === 0) return -1;
    const t = currentTime;
    let activeIdx = -1;

    for (let i = 0; i < translationChunks.length; i++) {
      const chunk = translationChunks[i];
      const parsed = parseTimestampToSec(chunk.timestamp);
      let start = parsed ? parsed.start : (chunk.start !== undefined ? Number(chunk.start) : i * 5);
      let end = parsed && parsed.end !== null ? parsed.end : (chunk.end !== undefined ? Number(chunk.end) : null);
      if (end === null) {
        if (i + 1 < translationChunks.length) {
          const nextParsed = parseTimestampToSec(translationChunks[i + 1].timestamp);
          end = nextParsed ? nextParsed.start : (i + 1) * 5;
        } else {
          end = start + 5;
        }
      }
      const effectiveStart = i === 0 ? Math.min(start, 0) : start;
      if (t >= effectiveStart && t < end) {
        activeIdx = i;
        break;
      }
      if (t >= effectiveStart) {
        activeIdx = i;
      }
    }
    return activeIdx;
  };

  const activeChunkIndex = getActiveChunkIndex();

  // Auto-scroll to keep active highlighted chunk visible in viewport during playback
  useEffect(() => {
    if (!isPlaying || activeChunkIndex === -1 || isUserEditingRef.current) return;
    const el = chunkItemRefs.current[activeChunkIndex];
    if (el && scrollContainerRef.current) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      });
    }
  }, [activeChunkIndex, isPlaying]);

  const handleSeekToChunk = (i) => {
    const chunk = translationChunks[i];
    const parsed = parseTimestampToSec(chunk?.timestamp);
    let targetSec = parsed ? parsed.start : (chunk?.start !== undefined ? Number(chunk.start) : i * 5);
    if (i === 0 && targetSec <= 1) {
      targetSec = 0;
    }
    if (audioRef.current) {
      if (!audioRef.current.src || audioRef.current.src === window.location.href) return;
      audioRef.current.currentTime = targetSec;
      setCurrentTime(targetSec);
      if (audioRef.current.paused) {
        try {
          const p = audioRef.current.play();
          if (p !== undefined) {
            p.then(() => {
              setIsPlaying(true);
            }).catch((err) => {
              if (err.name !== "AbortError") {
                console.warn("Audio play error:", err);
                try {
                  audioRef.current.load();
                  audioRef.current.play().then(() => setIsPlaying(true)).catch(() => { });
                } catch (_e) { }
              }
            });
          } else {
            setIsPlaying(true);
          }
        } catch (_e) { }
      }
    }
  };

  const transcriptionScroll = useCustomScrollbar(scrollContainerRef);

  // Re-size textareas and scrollbars when the tab becomes visible again.
  useEffect(() => {
    if (!isActive) return;
    const container = scrollContainerRef.current;
    if (container) {
      container.querySelectorAll("textarea").forEach((el) => {
        el.style.height = "auto";
        el.style.height = el.scrollHeight + "px";
      });
      container.scrollTop = 0;
    }
    transcriptionScroll.update();
    const t1 = setTimeout(() => {
      if (container) container.scrollTop = 0;
      transcriptionScroll.update();
    }, 100);
    return () => clearTimeout(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    const el = transcriptionRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    }
    transcriptionScroll.update();
    const t1 = setTimeout(() => { transcriptionScroll.update(); }, 100);
    return () => clearTimeout(t1);
  }, [transcriptionText, selectedTranslation, audioHistory]);

  // Auto-size all chunk textareas after DOM paint
  useEffect(() => {
    if (translationChunks.length === 0) return;
    const sizeAll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      container.querySelectorAll("textarea").forEach((el) => {
        el.style.height = "auto";
        el.style.height = el.scrollHeight + "px";
      });
    };
    const raf = requestAnimationFrame(() => {
      sizeAll();
      const t = setTimeout(sizeAll, 80);
      return () => clearTimeout(t);
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationChunks]);

  // Auto-scroll to bottom only when new translation content is actively streamed during recording/transcribing
  useEffect(() => {
    if (!isRecording && !isTranscribing && !isSending) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    if (isNearBottomRef.current && !isUserEditingRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [translationChunks, isRecording, isTranscribing, isSending]);

  // Track user scroll position to decide auto-scroll behavior
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const THRESHOLD = 80;

    const checkPosition = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      isNearBottomRef.current = scrollHeight - scrollTop - clientHeight <= THRESHOLD;
    };

    const handleWheel = () => {
      isUserEditingRef.current = true;
      checkPosition();
      clearTimeout(container._scrollTimeout);
      container._scrollTimeout = setTimeout(() => {
        checkPosition();
        isUserEditingRef.current = false;
      }, 1500);
    };

    const handleTouchStart = () => {
      isUserEditingRef.current = true;
      checkPosition();
      clearTimeout(container._touchTimeout);
      container._touchTimeout = setTimeout(() => {
        checkPosition();
        isUserEditingRef.current = false;
      }, 1500);
    };

    const handlePointerDown = () => {
      isUserEditingRef.current = true;
      checkPosition();
      clearTimeout(container._pointerTimeout);
      container._pointerTimeout = setTimeout(() => {
        checkPosition();
        isUserEditingRef.current = false;
      }, 1500);
    };

    container.addEventListener('wheel', handleWheel);
    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('scroll', checkPosition, { passive: true });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('scroll', checkPosition);
      clearTimeout(container._scrollTimeout);
      clearTimeout(container._touchTimeout);
      clearTimeout(container._pointerTimeout);
    };
  }, []);

  // Track when user focuses on textareas (editing)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleFocus = (e) => {
      if (e.target.tagName === 'TEXTAREA') {
        isUserEditingRef.current = true;
      }
    };

    const handleBlur = (e) => {
      if (e.target.tagName === 'TEXTAREA') {
        setTimeout(() => {
          isUserEditingRef.current = false;
        }, 500);
      }
    };

    container.addEventListener('focusin', handleFocus);
    container.addEventListener('focusout', handleBlur);

    return () => {
      container.removeEventListener('focusin', handleFocus);
      container.removeEventListener('focusout', handleBlur);
    };
  }, []);

  useEffect(() => {
    const item = latestAudioTranslation || (Array.isArray(audioHistory) && audioHistory.length > 0 ? audioHistory[0] : null);
    if (!item) return;

    // Do not overwrite during active recording session or active audio upload
    if (isRecordingActiveRef.current || isRecording || isUploadActiveRef.current) return;

    const text = item.transcription || "";
    const translation = item.translation || item.translatedText || "";
    const segments = Array.isArray(item.segments) ? item.segments : [];
    const itemChunks = Array.isArray(item.chunks) ? item.chunks : [];
    const hasData = !!(text || translation || segments.length > 0 || itemChunks.length > 0);

    // Item is processing if status is 'processing', 'pending', or isProcessing is true, and not completed/failed/done
    const isItemProcessing = (
      item.status === "processing" ||
      item.status === "pending" ||
      item.isProcessing === true
    ) && item.status !== "failed" && item.status !== "completed" && item.status !== "done" && item.status !== "error";

    if (isItemProcessing) {
      setIsTranscribing(true);
      const prog = typeof item.percentage === "number" ? item.percentage : (item.progress || 50);
      setTranscribeProgress(Math.min(99, Math.max(0, prog)));
      const audioSrc = item.audioFileName || item.audioUrl || item.audioFile;
      if (audioSrc) {
        setNewAudioUrl(audioSrc);
        calculateBlobDuration(audioSrc);
      }
      if (item.targetLanguage) {
        setSelectedLanguage(item.targetLanguage);
        selectedLanguageRef.current = item.targetLanguage;
      }
      return;
    }

    // If item is complete or has partial content (has transcription, translation, segments, or audioFileName)
    const id = item._id || item.id || null;
    const audioSrc = item.audioFileName || item.audioUrl || item.audioFile;

    if (text || translation || segments.length > 0 || itemChunks.length > 0 || audioSrc) {
      setIsTranscribing(false);
      setTranscribeProgress(100);
      if (id) currentRecordingIdRef.current = id;
      transcriptionTextRef.current = text;
      savedTranscriptionRef.current = text;
      savedTranslationRef.current = translation;
      setTranscriptionText(text);
      setSelectedTranslation(translation);

      let chunks = [];
      if (segments.length > 0) {
        chunks = segments.map((s, idx) => ({
          timestamp: s.timestamp || (s.start !== undefined && s.end !== undefined ? `${Math.round(s.start)}-${Math.round(s.end)}s` : `${idx * 5}-${(idx + 1) * 5}s`),
          translatedText: s.translatedText || s.translation || "",
          person: s.person || s.speaker || `Person 1`,
          originalText: s.originalText || "",
          start: s.start,
          end: s.end,
        }));
      } else if (itemChunks.length > 0) {
        chunks = itemChunks;
      } else if (currentChunksRef.current && currentChunksRef.current.length > 0) {
        // Keep all accumulated recorded chunks from localStorage
        chunks = currentChunksRef.current;
      } else if (translation) {
        chunks = [{
          timestamp: "0-10s",
          translatedText: translation,
          person: "Person 1",
          originalText: text,
        }];
      }

      currentChunksRef.current = chunks;
      setTranslationChunks(chunks);
      if (chunks.length > 0) {
        const lastTs = chunks[chunks.length - 1]?.timestamp || "";
        lastTimestampRef.current = lastTs;
        setLastTimestamp(lastTs);
      }

      if (audioSrc) {
        if (!audioUrlRef.current || !audioUrlRef.current.startsWith("blob:")) {
          setNewAudioUrl(audioSrc);
          calculateBlobDuration(audioSrc);
        }
      }
      if (item.targetLanguage) {
        setSelectedLanguage(item.targetLanguage);
        selectedLanguageRef.current = item.targetLanguage;
      }
      if (typeof item.personalIdentifiersProtected === "number") {
        setPersonalIdentifiersProtected(item.personalIdentifiersProtected);
        personalIdentifiersProtectedRef.current = item.personalIdentifiersProtected;
      }

      const totalWords = (translation || text).trim() === "" ? 0 : (translation || text).trim().split(/\s+/).length;
      setWordCount(totalWords);
      setIsCurrentTranscriptionSaved(true);
      setEmptyTranscriptionError(false);
      persistAll();
    }
  }, [latestAudioTranslation, audioHistory]);

  useEffect(() => {
    if (!isTranscribing) return;
    const interval = setInterval(() => {
      if (onTranscriptionCompleteRef.current) {
        onTranscriptionCompleteRef.current();
      }
    }, 2500);
    return () => clearInterval(interval);
    // Only depend on isTranscribing - use ref for the callback so we don't restart the interval on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTranscribing]);

  useEffect(() => {
    if (onHasContent) onHasContent(!!transcriptionText || !!uploadedFile || !!audioUrl);
  }, [transcriptionText, uploadedFile, audioUrl, onHasContent]);

  const filteredLanguages = countryData.filter((item) =>
    item.name.toLowerCase().includes(langSearch.toLowerCase())
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setIsLangOpen(false);
      }
    };
    if (isLangOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isLangOpen]);

  const handleLangSelect = (item) => {
    setSelectedLanguage(item.name);
    selectedLanguageRef.current = item.name;
    setIsLangOpen(false);
    setLangSearch("");
    setShowLangMsg(false);
    setRecordingError("");
  };

  // ============ SOCKET RECORDING FUNCTIONS ============
  const startSegmentRecorder = () => {
    if (!isRecordingActiveRef.current || isPausedRef.current || !audioStreamRef.current) return;
    const stream = audioStreamRef.current;
    const preferredType = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
      "audio/wav",
    ].find((type) => MediaRecorder.isTypeSupported(type));

    const segmentChunks = [];
    const segmentRecorder = new MediaRecorder(stream, preferredType ? { mimeType: preferredType } : undefined);
    currentSegmentRecorderRef.current = segmentRecorder;

    segmentRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        segmentChunks.push(event.data);
      }
    };

    segmentRecorder.onstop = async () => {
      if (segmentChunks.length > 0) {
        const type = segmentRecorder.mimeType || preferredType || "audio/webm";
        const extension = type.includes("ogg") ? "ogg" : type.includes("wav") ? "wav" : type.includes("mp4") ? "mp4" : "webm";
        const chunkBlob = new File(segmentChunks, `chunk-${Date.now()}.${extension}`, { type });
        if (chunkBlob.size > 800) {
          const base64Audio = await fileToBase64(chunkBlob);
          const sock = socketRef.current;
          if (sock && base64Audio) {
            setSocketProcessing(true);
            const currentChunkId = chunkIndexRef.current++;
            const startTime = (currentChunkId - 1) * 30 + 1;
            const endTime = currentChunkId * 30;
            sock.emit("translate-audio", {
              targetLanguage: selectedLanguageRef.current || selectedLanguage,
              audioFile: base64Audio,
              sessionId: sessionIdRef.current,
              chunkId: currentChunkId,
              startTime: startTime,
              endTime: endTime,
            });
          }
        }
      }
      if (isRecordingActiveRef.current && !isPausedRef.current) {
        startSegmentRecorder();
      }
    };

    segmentRecorder.start();
    if (segmentTimerRef.current) clearTimeout(segmentTimerRef.current);
    segmentTimerRef.current = setTimeout(() => {
      if (segmentRecorder.state === "recording") {
        segmentRecorder.stop();
      }
    }, 30000);
  };

  const handleRecord = async () => {
    if (!selectedLanguage) {
      setShowLangMsg(true);
      setRecordingError(t.selectAudioLangFirst || "Please select an audio language first");
      return;
    }
    setRecordingError("");
    setShowLangMsg(false);
    setUploadedFile(null);
    setEmptyTranscriptionError(false);
    setTranslationChunks([]);
    currentChunksRef.current = [];
    transcriptionTextRef.current = "";
    savedTranscriptionRef.current = "";
    savedTranslationRef.current = "";
    setTranscriptionText("");
    setSelectedTranslation("");
    setWordCount(0);
    lastTimestampRef.current = "";
    setLastTimestamp("");
    syncAudioUrl("");
    persistAll("");

    try {
      await connectSocket();
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setRecordingError(t.micNotAvailable);
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      sessionIdRef.current = generateSessionId();
      chunkIndexRef.current = 1;
      isRecordingActiveRef.current = true;
      isPausedRef.current = false;
      audioChunksRef.current = [];

      const preferredType = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
        "audio/wav",
      ].find((type) => MediaRecorder.isTypeSupported(type));

      const mainRecorder = new MediaRecorder(stream, preferredType ? { mimeType: preferredType } : undefined);
      mediaRecorderRef.current = mainRecorder;
      mainRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      mainRecorder.start(1000);

      startSegmentRecorder();

      setRecordingSeconds(0);
      recordingDurationRef.current = 0;
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => {
          const next = s + 1;
          recordingDurationRef.current = next;
          return next;
        });
      }, 1000);
      setIsRecording(true);
      setIsPaused(false);
    } catch (err) {
      const msg = err.message?.includes("Requested device not found")
        ? t.requestedDeviceNotFound
        : err.message;
      setRecordingError(t.micError + " " + msg);
    }
  };

  const handlePause = () => {
    isRecordingActiveRef.current = false;
    isPausedRef.current = true;
    setIsPaused(true);
    if (segmentTimerRef.current) {
      clearTimeout(segmentTimerRef.current);
      segmentTimerRef.current = null;
    }
    if (currentSegmentRecorderRef.current && currentSegmentRecorderRef.current.state === "recording") {
      currentSegmentRecorderRef.current.stop();
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      try { mediaRecorderRef.current.pause(); } catch (_e) { }
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const handleResume = () => {
    isRecordingActiveRef.current = true;
    isPausedRef.current = false;
    setIsPaused(false);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      try { mediaRecorderRef.current.resume(); } catch (_e) { }
    }
    startSegmentRecorder();
    recordingTimerRef.current = setInterval(() => {
      setRecordingSeconds((s) => {
        const next = s + 1;
        recordingDurationRef.current = next;
        return next;
      });
    }, 1000);
  };

  const handleStop = () => {
    isRecordingActiveRef.current = false;
    isPausedRef.current = false;
    setIsPaused(false);
    if (segmentTimerRef.current) {
      clearTimeout(segmentTimerRef.current);
      segmentTimerRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    const recordedSec = recordingDurationRef.current || recordingSeconds;
    setRecordingSeconds(0);
    setIsRecording(false);
    setAwaitingFinal(true);
    setSocketProcessing(true);

    if (recordedSec > 0) {
      setAudioDuration(recordedSec);
      audioDurationRef.current = recordedSec;
      recordingDurationRef.current = recordedSec;
    }

    if (currentSegmentRecorderRef.current && currentSegmentRecorderRef.current.state === "recording") {
      try { currentSegmentRecorderRef.current.stop(); } catch (_e) { }
    }

    const preferredType = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
      "audio/wav",
    ].find((type) => MediaRecorder.isTypeSupported(type));

    const stopTracks = () => {
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      }
    };

    const uploadFullRecording = async (fullBlob, mimeType) => {
      const sid = sessionIdRef.current;
      if (!sid || !fullBlob) return;
      try {
        const ext = mimeType.includes("ogg") ? "ogg" : mimeType.includes("wav") ? "wav" : mimeType.includes("mp4") ? "mp4" : "webm";
        const recordedFile = new File([fullBlob], `recording-${sid}.${ext}`, { type: mimeType });
        const res = await TranscribeService.uploadAudioBySession(sid, recordedFile);
        const serverAudio = res?.audioFileName || res?.data?.audioFileName || res?.audioUrl || res?.data?.audioUrl || res?.audioFile || res?.data?.audioFile;
        if (serverAudio) {
          setNewAudioUrl(serverAudio);
          persistAll(serverAudio);
          calculateBlobDuration(serverAudio);
        }
        if (onTranscriptionComplete) {
          onTranscriptionComplete();
        }
      } catch (err) {
        console.warn("Failed to upload recorded audio by session:", err);
      }
    };

    const mainRecorder = mediaRecorderRef.current;
    if (mainRecorder && mainRecorder.state !== "inactive") {
      mainRecorder.onstop = () => {
        if (audioChunksRef.current.length > 0) {
          const type = mainRecorder.mimeType || preferredType || "audio/webm";
          const fullBlob = new Blob(audioChunksRef.current, { type });
          const localUrl = URL.createObjectURL(fullBlob);
          setNewAudioUrl(localUrl);
          idbStoreFile(fullBlob);
          persistAll(localUrl);
          calculateBlobDuration(fullBlob);
          uploadFullRecording(fullBlob, type);
        }
        stopTracks();
      };
      try {
        if (mainRecorder.state === "recording") {
          mainRecorder.requestData();
        }
        mainRecorder.stop();
      } catch (_e) {
        stopTracks();
      }
    } else {
      if (audioChunksRef.current.length > 0) {
        const type = preferredType || "audio/webm";
        const fullBlob = new Blob(audioChunksRef.current, { type });
        const localUrl = URL.createObjectURL(fullBlob);
        setNewAudioUrl(localUrl);
        idbStoreFile(fullBlob);
        persistAll(localUrl);
        calculateBlobDuration(fullBlob);
        uploadFullRecording(fullBlob, type);
      }
      stopTracks();
    }
  };

  const performAudioTranscription = async (file) => {
    if (isTokenExhausted) {
      const msg = t.tokenLimitReached || "You have reached your token limit. Please upgrade your plan.";
      setRecordingError(msg);
      toastError(msg);
      return;
    }
    isUploadActiveRef.current = true;
    setRecordingError("");
    setShowLangMsg(false);
    setEmptyTranscriptionError(false);
    setTranslationChunks([]);
    currentChunksRef.current = [];
    setTranscribeProgress(0);
    const t0 = Date.now();
    const collectedChunks = [];
    let streamTranscription = "";
    let streamTranslation = "";

    try {
      try {
        localStorage.setItem(
          "linko_pending_transcribe",
          JSON.stringify({
            fileName: file.name,
            selectedLanguage: selectedLanguageRef.current || selectedLanguage,
            timestamp: Date.now(),
          })
        );
      } catch (_) {}
      setIsTranscribing(true);

      const result = await TranscribeService.translateAudioViaSocket(
        file,
        selectedLanguage,
        (chunk) => {
          // Update status indicator directly with the exact percentage from the API response
          if (chunk.type === "done" || chunk.percentage === 100) {
            setTranscribeProgress(100);
          } else {
            const apiPercentage = chunk.percentage ?? chunk.data?.percentage ?? null;
            if (apiPercentage !== null && apiPercentage !== undefined) {
              setTranscribeProgress(Math.min(99, Math.max(0, Number(apiPercentage))));
            }
          }

          if (chunk.type === "transcription" && chunk.transcription) {
            streamTranscription = chunk.transcription;
          }

          if (chunk.type === "chunk" && (chunk.data?.translatedText || chunk.data?.originalText)) {
            const c = {
              timestamp: chunk.data.timestamp || (chunk.data.start !== undefined && chunk.data.end !== undefined ? `${Math.round(chunk.data.start)}-${Math.round(chunk.data.end)}s` : ""),
              translatedText: chunk.data.translatedText || "",
              person: chunk.data.person || chunk.data.speaker || "Person 1",
              originalText: chunk.data.originalText || "",
              start: chunk.data.start,
              end: chunk.data.end,
            };
            collectedChunks.push(c);
          }
        },
        dispatch
      );

      // If an error or token limit was reached
      if (result?.error) {
        const errMsg = result.error;
        setRecordingError(errMsg);
        toastError(errMsg);
      }

      const responseAudio = result?.audioFileName || result?.audioUrl || result?.audioFile;
      if (responseAudio) {
        syncAudioUrl(responseAudio);
      }
      const pi = result?.personalIdentifiersProtected ?? null;
      if (typeof pi === "number") {
        setPersonalIdentifiersProtected(pi);
        personalIdentifiersProtectedRef.current = pi;
      }

      // Stream completed - set 100% and render immediately
      setTranscribeProgress(100);

      let chunks = collectedChunks;
      if (chunks.length === 0 && Array.isArray(result?.segments) && result.segments.length > 0) {
        chunks = result.segments.map((s, idx) => ({
          timestamp: s.timestamp || (s.start !== undefined && s.end !== undefined ? `${Math.round(s.start)}-${Math.round(s.end)}s` : `${idx * 5}-${(idx + 1) * 5}s`),
          translatedText: s.translatedText || s.translation || s.text || "",
          person: s.person || s.speaker || `Person 1`,
          originalText: s.originalText || s.transcription || s.text || "",
          start: s.start,
          end: s.end,
        }));
      }

      const text = result?.transcription || streamTranscription || (chunks.length > 0 ? chunks.map((c) => c.originalText).filter(Boolean).join(" ") : "");
      const translation = result?.translation || streamTranslation || (chunks.length > 0 ? chunks.map((c) => c.translatedText).filter(Boolean).join(" ") : "");
      const id = result?.id;
      const rt = ((Date.now() - t0) / 1000).toFixed(1);

      if (!text && !translation && chunks.length === 0) {
        setTranslationChunks([]);
        setEmptyTranscriptionError(true);
      } else {
        setEmptyTranscriptionError(false);
        if (id) currentRecordingIdRef.current = id;
        transcriptionTextRef.current = text;
        savedTranscriptionRef.current = text;
        savedTranslationRef.current = translation;
        currentChunksRef.current = chunks;
        currentResponseTimeRef.current = rt;
        lastTimestampRef.current = chunks.length > 0 ? chunks[chunks.length - 1].timestamp : "";
        setTranscriptionText(text);
        setSelectedTranslation(translation);
        setTranslationChunks(chunks);
        if (chunks.length > 0) {
          setLastTimestamp(chunks[chunks.length - 1].timestamp);
        }
        setIsTextDirty(false);
        setIsCurrentTranscriptionSaved(false);
        setResponseTime(rt);
        setWordCount((translation || text).trim() === "" ? 0 : (translation || text).trim().split(/\s+/).length);
        resetHistoryToCurrent();
        persistAll(responseAudio || audioUrl);
        if (onTranscriptionComplete) onTranscriptionComplete();
      }
    } catch (error) {
      const errMsg = error?.message || error?.response?.data?.message || t.audioTranscriptionFailed || t.somethingWentWrong;
      setRecordingError(errMsg);
      toastError(errMsg);

      let chunks = collectedChunks;
      const partial = error?.partialResult || {};
      if (chunks.length === 0 && Array.isArray(partial.segments) && partial.segments.length > 0) {
        chunks = partial.segments.map((s, idx) => ({
          timestamp: s.timestamp || (s.start !== undefined && s.end !== undefined ? `${Math.round(s.start)}-${Math.round(s.end)}s` : `${idx * 5}-${(idx + 1) * 5}s`),
          translatedText: s.translatedText || s.translation || s.text || "",
          person: s.person || s.speaker || `Person 1`,
          originalText: s.originalText || s.transcription || s.text || "",
          start: s.start,
          end: s.end,
        }));
      }

      const text = partial.transcription || streamTranscription || (chunks.length > 0 ? chunks.map((c) => c.originalText).filter(Boolean).join(" ") : "");
      const translation = partial.translation || partial.translatedText || streamTranslation || (chunks.length > 0 ? chunks.map((c) => c.translatedText).filter(Boolean).join(" ") : "");

      if (text || translation || chunks.length > 0) {
        setEmptyTranscriptionError(false);
        transcriptionTextRef.current = text;
        savedTranscriptionRef.current = text;
        savedTranslationRef.current = translation;
        currentChunksRef.current = chunks;
        setTranscriptionText(text);
        setSelectedTranslation(translation);
        setTranslationChunks(chunks);
        if (chunks.length > 0) {
          const lastTs = chunks[chunks.length - 1]?.timestamp || "";
          lastTimestampRef.current = lastTs;
          setLastTimestamp(lastTs);
        }
        setWordCount((translation || text).trim() === "" ? 0 : (translation || text).trim().split(/\s+/).length);
        setIsCurrentTranscriptionSaved(true);
        const responseAudio = partial.audioFileName || partial.audioUrl || partial.audioFile;
        if (responseAudio) {
          setNewAudioUrl(responseAudio);
        }
        persistAll(responseAudio || audioUrl);
        if (onTranscriptionComplete) onTranscriptionComplete();
      }
    } finally {
      try {
        localStorage.removeItem("linko_pending_transcribe");
        localStorage.removeItem("linko_transcribe_progress");
      } catch (_) {}
      isUploadActiveRef.current = false;
      setIsTranscribing(false);
      setTranscribeProgress(0);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = null;
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setNewAudioUrl(localUrl);
    setUploadedFile(file);
    idbStoreFile(file);
    calculateBlobDuration(file);
    setShowLangMsg(false);
    setRecordingError("");
    setEmptyTranscriptionError(false);
    setTranscriptionText("");
    setSelectedTranslation("");
    setTranslationChunks([]);
    transcriptionTextRef.current = "";
    savedTranslationRef.current = "";
    currentChunksRef.current = [];
    currentResponseTimeRef.current = null;
    lastTimestampRef.current = "";
    setWordCount(0);
  };

  const handleTranscribeClick = async () => {
    if (!uploadedFile) return;
    if (!selectedLanguage) {
      setShowLangMsg(true);
      const langErr = t.selectAudioLangFirst || t.selectAudioLangUpload || "Please select an audio language first";
      setRecordingError(langErr);
      toastError(langErr);
      return;
    }
    if (isTokenExhausted) {
      const msg = t.tokenLimitReached || "You have reached your token limit. Please upgrade your plan.";
      setRecordingError(msg);
      toastError(msg);
      return;
    }
    await performAudioTranscription(uploadedFile);
  };

  const handleExport = () => {
    if (!selectedTranslation) return;
    setShowExportPicker(true);
  };

  const handleSendForTranslation = () => {
    setShowExportPicker(false);
    const chunks = translationChunks.length > 0 ? translationChunks : currentChunksRef.current;
    let textToTranslate = "";
    if (chunks && chunks.length > 0) {
      const paragraphs = groupChunksIntoParagraphs(chunks);
      textToTranslate = paragraphs
        .map((para) =>
          para
            .map(
              (c) =>
                `${formatInlineTimestamp(c.timestamp, c.globalIndex)} ${c.translatedText}`
            )
            .join(" ")
        )
        .join("\n\n");
    } else {
      textToTranslate = selectedTranslation || transcriptionText || "";
    }
    const cleanText = textToTranslate
      .replace(/<\/?mark[^>]*>/gi, "")
      .replace(/<[^>]+>/g, "")
      .trim();

    if (!cleanText) return;

    if (onSendToTranslate) {
      onSendToTranslate(cleanText);
    }
  };

  const doExport = (format) => {
    setShowExportPicker(false);

    const exportText = selectedTranslation || transcriptionText;

    if (!exportText) return;

    const hasChunks = translationChunks.length > 0;

    const formatTimestamp = (ts) =>
      ts.replace(/(\d+)-(\d+)s/g, (_, a, b) => {
        const toMMSS = (n) => `${String(Math.floor(Number(n) / 60)).padStart(2, "0")}:${String(Number(n) % 60).padStart(2, "0")}`;
        return `${toMMSS(a)} – ${toMMSS(b)}`;
      });

    if (format === "text") {
      let textContent = "";
      if (hasChunks) {
        const paragraphs = groupChunksIntoParagraphs(translationChunks);
        textContent = paragraphs
          .map((para) =>
            para
              .map(
                (c) =>
                  `${formatInlineTimestamp(c.timestamp, c.globalIndex)} ${c.translatedText}`
              )
              .join(" ")
          )
          .join("\n\n");
      } else {
        textContent = exportText;
      }
      const cleanText = textContent.replace(/<\/?mark[^>]*>/gi, "").replace(/<[^>]+>/g, "").trim();
      const blob = new Blob([cleanText], { type: "text/plain" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `Transcription_${getTimestamp()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(() => URL.revokeObjectURL(url), 100);
    } else if (format === "word") {
      const formatHtml = (s) => {
        if (!s) return "";
        const clean = DOMPurify.sanitize(s, {
          ALLOWED_TAGS: ["mark", "span", "b", "strong", "i", "em", "br", "p", "u", "font"],
          ALLOWED_ATTR: ["style", "class", "color"],
        });
        return clean.replace(
          /<mark[^>]*>(.*?)<\/mark>/gi,
          '<span style="color:#3D2B90;font-weight:bold;"><font color="#3D2B90"><b>$1</b></font></span>'
        );
      };
      let contentHtml = "";

      if (hasChunks) {
        const paragraphs = groupChunksIntoParagraphs(translationChunks);
        paragraphs.forEach((para) => {
          const paraHtml = para
            .map(
              (c) =>
                `<span style="color:#716E89;font-weight:600;">[${formatInlineTimestamp(c.timestamp, c.globalIndex)}]</span> ${formatHtml(c.translatedText)}`
            )
            .join(" ");
          contentHtml += `<p style="margin-bottom:12pt;font-size:11pt;line-height:1.75;color:#2C234D;">${paraHtml}</p>`;
        });
      } else {
        contentHtml = `<div style="font-size:11pt;white-space:pre-wrap;line-height:1.75;color:#2C234D;">${formatHtml(exportText)}</div>`;
      }
      const htmlParts = [contentHtml];

      generateWordFromHtml(htmlParts, `Transcription_${getTimestamp()}.docx`);
    } else {
      const formatHtml = (s) => {
        if (!s) return "";
        const clean = DOMPurify.sanitize(s, {
          ALLOWED_TAGS: ["mark", "span", "b", "strong", "i", "em", "br", "p", "u", "font"],
          ALLOWED_ATTR: ["style", "class", "color"],
        });
        return clean.replace(
          /<mark[^>]*>(.*?)<\/mark>/gi,
          '<span style="color:#3D2B90;font-weight:bold;"><font color="#3D2B90"><b>$1</b></font></span>'
        );
      };
      let contentHtml = "";

      if (hasChunks) {
        const paragraphs = groupChunksIntoParagraphs(translationChunks);
        paragraphs.forEach((para) => {
          const paraHtml = para
            .map(
              (c) =>
                `<span style="color:#716E89;font-weight:600;">${formatInlineTimestamp(c.timestamp, c.globalIndex)}</span> ${formatHtml(c.translatedText)}`
            )
            .join(" ");
          contentHtml += `<p style="margin-bottom:14px;font-size:11px;line-height:1.75;color:#2C234D;" data-pdf-keep-together="1">${paraHtml}</p>`;
        });
      } else {
        contentHtml = `<div style="font-size:11px;white-space:pre-wrap;line-height:1.75;color:#2C234D;">${formatHtml(exportText)}</div>`;
      }
      const htmlParts = [`${MD_PDF_STYLE}${contentHtml}`];

      generatePdfFromHtml(htmlParts, `Transcription_${getTimestamp()}.pdf`);
    }
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    const container = scrollContainerRef.current;
    if (container) {
      isUserEditingRef.current = true;
      clearTimeout(container._scrollTimeout);
      container._scrollTimeout = setTimeout(() => { isUserEditingRef.current = false; }, 2000);
    }
    savedTranslationRef.current = text;
    setSelectedTranslation(text);
    setWordCount(text.trim() === "" ? 0 : text.trim().split(/\s+/).length);
    setIsTextDirty(true);
    persistAll();
    syncUndoRedo();
    requestAnimationFrame(() => {
      const el = transcriptionRef.current;
      if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; }
      transcriptionScroll.update();
    });
  };

  const handleSave = async () => {
    const lang = selectedLanguageRef.current;
    const editedTranslation = savedTranslationRef.current;

    if (!lang || !editedTranslation.trim()) return;

    setIsSaving(true);
    try {
      commitCurrentToHistory();
      setIsTextDirty(false);
      setIsCurrentTranscriptionSaved(true);
      toastSuccess(t.saveSuccess || "Saved successfully");
    } catch (_e) {
    } finally {
      setIsSaving(false);
      setIsRetranslating(false);
    }
  };
  const isLoading = isTranscribing || isSending || socketProcessing || awaitingFinal;

  // Track transcription history for undo
  const transcriptionHistoryRef = useRef([]);

  const updateWordCount = (text) => {
    const words = text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
    setWordCount(words);
  };

  // Distinct color palette per person
  const PERSON_COLORS = [
    { bg: "#3D2B90", light: "#EDE9F8", text: "#3D2B90" },
    { bg: "#EE7289", light: "#FEE8ED", text: "#C94B65" },
    { bg: "#1E9E8C", light: "#D4F5F0", text: "#1E9E8C" },
    { bg: "#E08C2A", light: "#FEF0D8", text: "#B36A10" },
    { bg: "#6B4FBB", light: "#EFE9FF", text: "#6B4FBB" },
  ];

  const getPersonColor = (personStr, fallbackIndex) => {
    const match = personStr && personStr.match(/(\d+)$/);
    const num = match ? parseInt(match[1], 10) - 1 : fallbackIndex;
    return PERSON_COLORS[num % PERSON_COLORS.length];
  };

  const EmptyState = () => (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex flex-col items-center justify-center flex-1 gap-5 py-8">
        <div className="flex items-center justify-center w-[90px] h-[90px] rounded-full bg-[#EDE9F8]">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="9" y="2" width="6" height="12" rx="3" fill="#3D2B90" />
            <path d="M5 11a7 7 0 0 0 14 0" stroke="#3D2B90" strokeWidth="1.8" strokeLinecap="round" />
            <line x1="12" y1="18" x2="12" y2="22" stroke="#3D2B90" strokeWidth="1.8" strokeLinecap="round" />
            <line x1="8" y1="22" x2="16" y2="22" stroke="#3D2B90" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <p className="font-roboto font-bold text-[14px] text-[#42445A] uppercase tracking-wide text-center">
            {t.noAudioUploaded}
          </p>
          <p className="font-roboto text-[12px] text-[#9190A4] text-center max-w-[260px] leading-relaxed">
            {t.noAudioUploadedSubtitle}
          </p>
        </div>
        <button
          onClick={() => {
            audioFileRef.current?.click();
          }}
          disabled={isLoading || isRecording}
          className={`flex items-center gap-[8.44px] h-[34px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none py-[12.66px] px-[22px] whitespace-nowrap ${isLoading || isRecording ? "opacity-50 cursor-not-allowed" : "hover:bg-[#2e1f72] transition-colors"}`}
        >
          <img src={UploadFileIcon} alt="upload" className="h-3.5 w-3.5 object-contain flex-shrink-0" />
          {t.uploadAudioFile}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex flex-col flex-1 min-h-0 w-full bg-white rounded-[27px] p-3 sm:p-4">

        {/* Audio Language + Record/Stop toolbar */}
        <div className="relative flex items-center justify-center px-3 sm:px-4 mb-2 sm:mb-4 bg-deep-purple rounded-[20px] w-full min-h-[40.95px] py-2 sm:py-0 flex-shrink-0">
          <div
            className="absolute right-3 lg:right-4 top-2 sm:top-1/2 sm:-translate-y-1/2"
            onMouseEnter={() => !hasAiAccess && setShowAiTooltip(true)}
            onMouseLeave={() => setShowAiTooltip(false)}
          >
            <button
              type="button"
              onClick={() => {
                if (hasAiAccess) {
                  setAiMenuOpen(true);
                } else if (isContactRequestSent()) {
                  setShowAlreadySentPopup(true);
                } else {
                  setShowContactUs(true);
                }
              }}
              className="cursor-pointer"
              aria-label="Open AI menu"
            >
              <img src={AIIcon} alt="AI" className="w-[26px] lg:w-[40.37px] h-[20px] lg:h-[31.22px] object-contain" />
            </button>
            {showAiTooltip && !hasAiAccess && (
              <span className="absolute right-0 top-full mt-2 z-50 px-2.5 py-1.5 rounded bg-white text-gray-900 text-[10px] leading-snug pointer-events-none shadow-md border border-gray-200 max-w-[280px]">
                {t.aiRulesOrgsOnly}
              </span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-2 w-auto">

            {/* Audio Language dropdown */}
            <div className="relative" ref={langDropdownRef}>
              <button
                onClick={() => setIsLangOpen((v) => !v)}
                className="flex items-center gap-1.5 h-[24.93px] px-2 sm:px-3 lg:px-4 bg-white rounded-xl font-roboto font-bold text-[11px] sm:text-[12px] lg:text-[14.66px] text-deep-purple uppercase leading-none"
              >
                <img src={DropdownIcon} alt="dropdown" className="h-3 w-3 flex-shrink-0 object-contain" />
                <span className="whitespace-nowrap">{selectedLanguage || t.audioLanguage}</span>
              </button>
              {isLangOpen && (
                <div
                  className="absolute left-0 mt-2 w-52 bg-white rounded-xl shadow-lg z-50 border border-border"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-2">
                    <input
                      type="text"
                      placeholder={t.searchLanguage}
                      value={langSearch}
                      onChange={(e) => setLangSearch(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="max-h-44 overflow-y-auto dropdown-scroll">
                    {filteredLanguages.length > 0 ? (
                      filteredLanguages.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleLangSelect(item)}
                          className={`flex items-center justify-between w-full text-left px-4 py-2 text-xs border-b border-border last:border-0 ${item.name === selectedLanguage ? "text-[#3D2B90] font-bold bg-[#F3F1F9]" : "text-gray-700 hover:bg-gray"}`}
                        >
                          <span>{item.name}</span>
                          {item.name === selectedLanguage && (
                            <svg className="w-3.5 h-3.5 text-[#3D2B90] flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      ))
                    ) : (
                      <p className="px-4 py-2 text-xs text-gray-500 text-center">{t.noLanguageFound}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Record button */}
            <button
              onClick={() => {
                if (socketProcessing || awaitingFinal || isTranscribing) return;
                if (!selectedLanguage) {
                  setShowLangMsg(true);
                  setRecordingError(t.selectAudioLangFirst || "Please select an audio language first");
                  return;
                }
                if (audioUrl || uploadedFile || transcriptionText || translationChunks.length > 0 || selectedTranslation) {
                  confirmActionRef.current = "record";
                  setShowNewTranscribeConfirm(true);
                } else {
                  handleRecord();
                }
              }}
              disabled={(isRecording && !isPaused) || isLoading || isTokenExhausted || socketProcessing || awaitingFinal || isTranscribing}
              className={`flex items-center justify-center gap-1.5 h-[28px] px-3.5 rounded-full bg-[#C53F5C] hover:bg-[#d44866] font-roboto font-bold text-[11px] sm:text-[12px] text-white uppercase leading-none transition-colors ${((isRecording && !isPaused) || isLoading || isTokenExhausted || socketProcessing || awaitingFinal || isTranscribing) ? "opacity-80 cursor-not-allowed" : ""}`}
            >
              {(socketProcessing || awaitingFinal || isTranscribing) && !isRecording ? (
                <>
                  <span className="w-2.5 h-2.5 border-2 border-white/40 border-t-white rounded-full animate-spin flex-shrink-0" />
                  <span>{t.processing || "PROCESSING..."}</span>
                </>
              ) : isRecording && !isPaused ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-white flex-shrink-0 animate-pulse" />
                  <span>{formatRecordingTime(recordingSeconds)}</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-white flex-shrink-0" />
                  <span>{t.record || "RECORD"}</span>
                </>
              )}
            </button>

            {/* Pause button */}
            <button
              onClick={isPaused ? handleResume : handlePause}
              disabled={!isRecording}
              className={`flex items-center justify-center gap-1.5 h-[28px] px-3.5 rounded-full bg-[#7D53C5] hover:bg-[#8d62d6] font-roboto font-bold text-[11px] sm:text-[12px] text-white uppercase leading-none transition-colors ${!isRecording ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <svg className="w-2.5 h-2.5 text-white flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
              <span>{isPaused ? "RESUME" : (t.pause || "PAUSE")}</span>
            </button>

            {/* Stop button */}
            <button
              onClick={handleStop}
              disabled={!isRecording}
              className={`flex items-center justify-center gap-1.5 h-[28px] px-3.5 rounded-full bg-[#534E71] hover:bg-[#635d82] font-roboto font-bold text-[11px] sm:text-[12px] text-white uppercase leading-none transition-colors ${!isRecording ? "opacity-30 cursor-not-allowed" : ""}`}
            >
              <svg className="w-2.5 h-2.5 text-white flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h12v12H6z" />
              </svg>
              <span>{t.stop || "STOP"}</span>
            </button>


          </div>
        </div>

        {/* Error messages */}
        {recordingError && (
          <div className="mb-2 px-4 py-2.5 bg-[#FFF3CD] border border-[#FBBF24] rounded-xl flex items-center gap-2 flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className="flex-shrink-0">
              <path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 4a1 1 0 011 1v4a1 1 0 11-2 0V7a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z" fill="#B45309" />
            </svg>
            <span className="font-roboto text-[11px] font-semibold text-[#92400E]">
              {recordingError}
            </span>
            <button onClick={() => setRecordingError("")} className="ml-auto flex-shrink-0 text-[#92400E] hover:opacity-70">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}
        {emptyTranscriptionError && !recordingError && (
          <div className="mb-2 px-4 py-2.5 bg-[#FFF3CD] border border-[#FBBF24] rounded-xl flex items-center gap-2 flex-shrink-0">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className="flex-shrink-0">
              <path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 4a1 1 0 011 1v4a1 1 0 11-2 0V7a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z" fill="#B45309" />
            </svg>
            <span className="font-roboto text-[11px] font-semibold text-[#92400E]">
              {t.noSpeechDetected}
            </span>
            <button onClick={() => setEmptyTranscriptionError(false)} className="ml-auto flex-shrink-0 text-[#92400E] hover:opacity-70">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}

        {/* Main transcription panel */}
        <div className="bg-white rounded-[24px] border border-[#E2DCF0] p-4 sm:p-5 flex flex-col w-full flex-1 min-h-0 shadow-sm">

          {/* Upload Audio File button commented out: the empty-state placeholder has its own upload button
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => {
                if (!selectedLanguage) {
                  setIsLangOpen(true);
                  toastError(t.selectAudioLangUpload || "Please select an audio language first");
                  return;
                }
                if (transcriptionText || translationChunks.length > 0) {
                  confirmActionRef.current = "upload";
                  setShowNewTranscribeConfirm(true);
                } else {
                  audioFileRef.current?.click();
                }
              }}
              disabled={isLoading || isRecording || availableTokens === 0}
              className={`flex items-center gap-2 h-[34px] rounded-full bg-[#E8F1FF] border border-[#C5DCFF] text-[#3D2B90] hover:bg-[#d8e6ff] font-roboto font-bold text-xs uppercase leading-none px-4 sm:px-5 whitespace-nowrap transition-all shadow-sm ${isLoading || isRecording || availableTokens === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <img src={UploadFileIcon} alt="upload" className="h-3.5 w-3.5 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(18%) sepia(60%) saturate(2200%) hue-rotate(240deg) brightness(85%)" }} />
              <span className="hidden sm:inline">{t.uploadAudioFile}</span>
              <span className="sm:hidden">{t.uploadAudio}</span>
            </button>
          </div>
          */}
          <input
            ref={audioFileRef}
            type="file"
            hidden
            accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac"
            onChange={handleAudioUpload}
          />

          {/* Audio Player Bar - shown when audio file/url is present */}
          {audioUrl && (
            <div className="flex items-center gap-3 px-4 py-2.5 bg-[#F8F6FC] rounded-xl mb-3 border border-[#E2DCF0] flex-shrink-0">
              <audio
                ref={audioRef}
                src={audioUrl}
                preload="auto"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onError={() => {
                  // Guard against re-entry: only attempt IDB fallback once per audio URL
                  if (audioErrorFallbackRef.current) return;
                  audioErrorFallbackRef.current = true;
                  idbGetFile().then((file) => {
                    if (file && (file instanceof Blob || file.type)) {
                      const u = URL.createObjectURL(file);
                      syncAudioUrl(u);
                      calculateBlobDuration(file);
                    }
                  }).catch(() => { });
                }}
                onTimeUpdate={() => {
                  if (audioRef.current) {
                    const ct = audioRef.current.currentTime || 0;
                    setCurrentTime(ct);
                    const dur = audioRef.current.duration;
                    if (isFinite(dur) && dur > 0 && (!isFinite(audioDurationRef.current) || audioDurationRef.current <= 0)) {
                      setAudioDuration(dur);
                      audioDurationRef.current = dur;
                    }
                  }
                }}
                onLoadedMetadata={() => {
                  const el = audioRef.current;
                  if (!el) return;
                  if (isFinite(el.duration) && el.duration > 0) {
                    setAudioDuration(el.duration);
                    audioDurationRef.current = el.duration;
                  }
                }}
                onEnded={() => {
                  setIsPlaying(false);
                  setCurrentTime(0);
                }}
              />
              <button
                type="button"
                onClick={async () => {
                  const audio = audioRef.current;
                  if (!audio || !audio.src || audio.src === window.location.href) return;
                  if (!audio.paused) {
                    audio.pause();
                    setIsPlaying(false);
                  } else {
                    if (audio.ended || (isFinite(audio.duration) && audio.currentTime >= audio.duration - 0.1)) {
                      audio.currentTime = 0;
                      setCurrentTime(0);
                    }
                    try {
                      const p = audio.play();
                      if (p !== undefined) {
                        await p;
                        setIsPlaying(true);
                      } else {
                        setIsPlaying(true);
                      }
                    } catch (err) {
                      if (err.name !== "AbortError") {
                        console.warn("Audio play retry:", err);
                        try {
                          audio.load();
                          await audio.play();
                          setIsPlaying(true);
                        } catch (_e) { }
                      }
                    }
                  }
                }}
                className="w-8 h-8 rounded-full bg-[#3D2B90] text-white flex items-center justify-center hover:bg-[#2c1e6b] transition-all flex-shrink-0 shadow-sm"
              >
                {isPlaying ? (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6zm8 0h4v16h-4z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
              <span className="font-roboto font-semibold text-xs text-[#3D2B90] tabular-nums flex-shrink-0">
                {formatAudioTime(currentTime)}
              </span>
              <div className="relative flex-1 h-1.5 rounded-lg bg-[#C7BFE4] cursor-pointer flex-shrink-0">
                {(() => {
                  const d = getEffectiveDuration();
                  const pct = d > 0 ? Math.min(Math.max(0, (currentTime || 0) / d * 100), 100) : 0;
                  return (
                    <>
                      <div className="absolute inset-y-0 left-0 rounded-lg bg-[#3D2B90] pointer-events-none transition-all duration-75" style={{ width: `${pct}%` }} />
                      <div
                        className="pointer-events-none absolute top-1/2 w-3 h-3 -ml-1.5 rounded-full bg-[#3D2B90] border-2 border-white shadow-md -translate-y-1/2"
                        style={{ left: `${pct}%` }}
                      />
                    </>
                  );
                })()}
                <input
                  type="range"
                  min="0"
                  max={getEffectiveDuration() || 100}
                  step="0.05"
                  value={currentTime || 0}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (audioRef.current) {
                      audioRef.current.currentTime = val;
                      setCurrentTime(val);
                    }
                  }}
                  className="absolute inset-0 w-full h-[20px] -top-[7px] opacity-0 cursor-pointer appearance-none m-0 z-10"
                />
              </div>
              <span className="font-roboto font-semibold text-xs text-[#9190A4] tabular-nums flex-shrink-0">
                {formatAudioTime(getEffectiveDuration())}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (!audioRef.current) return;
                  audioRef.current.muted = !isMuted;
                  setIsMuted(!isMuted);
                }}
                className="text-[#3D2B90] hover:opacity-80 transition-opacity flex-shrink-0"
              >
                {isMuted ? (
                  <VolumeX className="w-4 h-4 text-[#3D2B90]" />
                ) : (
                  <Volume2 className="w-4 h-4 text-[#3D2B90]" />
                )}
              </button>
            </div>
          )}

          {/* Scrollable content area */}
          <div className="flex gap-[10.18px] flex-1 min-h-0 w-full">
            {/* Single Column Timestamped Transcript view */}
            <div ref={scrollContainerRef} className="relative bg-transparent rounded-[18px] flex flex-col flex-1 min-h-0 overflow-y-auto no-scrollbar">

              {/* Show loader when retranslating */}
              {isRetranslating ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-white/90 rounded-[18px]">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-[#3D2B90]/20 border-t-[#3D2B90] rounded-full animate-spin" />
                    <p className="text-[#3D2B90] text-sm font-semibold">
                      {t.retranslating}
                    </p>
                  </div>
                </div>
              ) : isTranscribing ? (
                /* Status percentage when uploading / transcribing file - don't display text yet */
                <div className="flex flex-col items-center justify-center flex-1 gap-6 py-8">
                  <div className="relative flex items-center justify-center w-[100px] h-[100px]">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                      <path
                        className="text-[#EDE9F8]"
                        strokeWidth="3.2"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className="text-[#3D2B90] transition-all duration-300 ease-out"
                        strokeDasharray={`${Math.round(transcribeProgress)}, 100`}
                        strokeWidth="3.2"
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span className="absolute font-roboto font-bold text-[18px] text-[#3D2B90]">
                      {Math.round(transcribeProgress)}%
                    </span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <p className="font-roboto font-bold text-[15px] text-[#42445A] uppercase tracking-wide text-center">
                      {t.transcribing || "Transcribing Audio..."}
                    </p>
                    <p className="font-roboto text-[12px] text-[#9190A4] text-center max-w-[280px] leading-relaxed">
                      {t.transcribingPleaseWait || "Please wait while we translate your audio..."}
                    </p>
                  </div>
                  {/* <div className="w-full max-w-[220px] h-2 bg-[#EDE9F8] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#3D2B90] rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${Math.round(transcribeProgress)}%` }}
                    />
                  </div> */}
                </div>
              ) : (transcriptionText || translationChunks.length > 0 || selectedTranslation) ? (
                <div className="flex flex-col gap-2 w-full py-1">
                  {translationChunks.length > 0 ? (
                    <div className="flex flex-col gap-5 w-full py-2 px-1 sm:px-2">
                      {groupChunksIntoParagraphs(translationChunks).map((para, pIdx) => (
                        <p
                          key={pIdx}
                          className="font-roboto text-[14px] sm:text-[15px] text-[#2C234D] leading-[1.8] select-text m-0"
                        >
                          {para.map((chunk) => {
                            const i = chunk.globalIndex;
                            const isCurrentActive = isPlaying && activeChunkIndex === i;
                            return (
                              <span
                                key={i}
                                ref={(el) => { chunkItemRefs.current[i] = el; }}
                                onClick={() => handleSeekToChunk(i)}
                                className={`cursor-pointer rounded-[4px] transition-all duration-150 inline ${isCurrentActive
                                  ? "bg-[#EDE9F8] text-[#160041] font-semibold px-1.5 py-0.5 shadow-xs"
                                  : "hover:bg-[#F3F1F9] px-0.5"
                                  }`}
                              >
                                <span
                                  className={`font-roboto tracking-tight select-none mr-1.5 transition-colors inline ${isCurrentActive
                                    ? "text-[#3D2B90] font-bold"
                                    : "text-[#716E89] font-normal hover:text-[#3D2B90]"
                                    }`}
                                >
                                  {formatInlineTimestamp(chunk.timestamp, i)}
                                </span>
                                <span className="inline">
                                  {renderHighlightedText(chunk.translatedText)}
                                </span>
                                {" "}
                              </span>
                            );
                          })}
                        </p>
                      ))}
                      {/* Streaming indicator */}
                      {(isLoading || isRecording || isTranscribing) && (
                        <div className="flex items-center justify-center gap-2 py-3">
                          <div className="w-2 h-2 bg-[#3D2B90] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <div className="w-2 h-2 bg-[#3D2B90] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <div className="w-2 h-2 bg-[#3D2B90] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-full flex flex-col gap-2">
                      <textarea
                        ref={transcriptionRef}
                        className="w-full bg-transparent resize-none focus:outline-none font-roboto font-medium text-[13px] sm:text-[14px] text-[#2C234D] leading-relaxed no-scrollbar overflow-hidden"
                        style={{ minHeight: "100px" }}
                        value={selectedTranslation}
                        onChange={handleTextChange}
                        disabled={isRetranslating}
                        spellCheck={false}
                        placeholder=""
                      />
                      {(isLoading || isRecording || isTranscribing) && (
                        <div className="flex items-center justify-center gap-2 py-3">
                          <div className="w-2 h-2 bg-[#3D2B90] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <div className="w-2 h-2 bg-[#3D2B90] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <div className="w-2 h-2 bg-[#3D2B90] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // Empty state
                isRecording ? (
                  <div className="flex flex-col items-center justify-center flex-1 gap-6 py-8">
                    <div className="flex items-end gap-[5px] h-[56px]">
                      {[0.4, 0.7, 1, 0.6, 0.9, 0.5, 0.8, 1, 0.6, 0.75, 0.45, 0.85].map((base, i) => (
                        <div
                          key={i}
                          className="w-[5px] rounded-full bg-[#3D2B90]"
                          style={{
                            height: `${Math.round(base * 56)}px`,
                            animation: `recordingBar 0.9s ease-in-out ${(i * 0.07).toFixed(2)}s infinite alternate`,
                            opacity: 0.7 + base * 0.3,
                            transformOrigin: "bottom",
                          }}
                        />
                      ))}
                    </div>
                    <div className="flex flex-col items-center gap-1.5">
                      <p className="font-roboto font-bold text-[22px] text-[#3D2B90] tabular-nums leading-none">
                        {formatRecordingTime(recordingSeconds)}
                      </p>
                      <p className="font-roboto text-[12px] text-[#9190A4] text-center max-w-[260px] leading-relaxed">
                        {t.transcribingPleaseWait || "Please wait while we translate your audio..."}
                      </p>
                    </div>
                  </div>
                ) : isLoading ? (
                  <div className="flex flex-col items-center justify-center flex-1 gap-5 py-8">
                    <div className="flex items-center justify-center w-[90px] h-[90px] rounded-full ">
                      <div className="w-10 h-10 border-4 border-[#3D2B90]/20 border-t-[#3D2B90] rounded-full animate-spin" />
                    </div>
                    <div className="flex flex-col items-center gap-1.5">
                      <p className="font-roboto font-bold text-[14px] text-[#42445A] uppercase tracking-wide text-center">
                        {socketProcessing ? "Processing your recording..." : t.processingAudio}
                      </p>
                      <p className="font-roboto text-[12px] text-[#9190A4] text-center max-w-[260px] leading-relaxed">
                        {socketProcessing ? "Please wait while we translate your audio..." : t.transcribingPleaseWait}
                      </p>
                    </div>
                  </div>
                ) : uploadedFile ? (
                  <div className="flex flex-col items-center justify-center flex-1 gap-5 py-8">
                    <div className="flex items-center justify-center w-[90px] h-[90px] rounded-full bg-[#EDE9F8]">
                      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="9" y="2" width="6" height="12" rx="3" fill="#3D2B90" />
                        <path d="M5 11a7 7 0 0 0 14 0" stroke="#3D2B90" strokeWidth="1.8" strokeLinecap="round" />
                        <line x1="12" y1="18" x2="12" y2="22" stroke="#3D2B90" strokeWidth="1.8" strokeLinecap="round" />
                        <line x1="8" y1="22" x2="16" y2="22" stroke="#3D2B90" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </div>
                    <div className="flex flex-col items-center gap-1.5">
                      <p className="font-roboto font-bold text-[14px] text-[#42445A] uppercase tracking-wide text-center break-all max-w-full px-4">
                        {uploadedFile.name}
                      </p>
                      <p className="font-roboto text-[12px] text-[#9190A4] text-center max-w-[280px] leading-relaxed">
                        {t.selectAudioLangAndTranscribe || "Select an audio language and click Transcribe to start"}
                      </p>
                    </div>
                    <button
                      onClick={handleTranscribeClick}
                      disabled={isLoading || isRecording}
                      className={`flex items-center gap-2 h-[34px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-6 whitespace-nowrap ${isLoading || isRecording ? "opacity-50 cursor-not-allowed" : "hover:bg-[#2e1f72] transition-colors"}`}
                    >
                      {t.transcribe || "Transcribe"}
                    </button>
                  </div>
                ) : (
                  <EmptyState />
                )
              )}
            </div>

            {/* Custom scrollbar track */}
            <div
              ref={transcriptionScroll.trackRef}
              className="block relative w-[17.94px] h-full bg-[#C7BFE4] rounded-[17px] flex-shrink-0 cursor-pointer"
              style={{ visibility: transcriptionScroll.hasOverflow ? "visible" : "hidden" }}
              onPointerDown={transcriptionScroll.onTrackPointerDown}
            >
              {transcriptionScroll.hasOverflow && (
                <div
                  ref={transcriptionScroll.thumbRef}
                  className="absolute w-full bg-[#3D2B90] rounded-[10px] cursor-grab active:cursor-grabbing"
                  style={{ top: `${transcriptionScroll.thumbTop}px`, height: `${transcriptionScroll.thumbH}px` }}
                  onPointerDown={transcriptionScroll.onThumbPointerDown}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </div>
          </div>

          {/* Word count + copy - single row: words left, copy icon right */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#F0ECF8] flex-shrink-0">
            <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
              <span className="font-roboto font-semibold text-xs text-[#9190A4] uppercase tracking-wide">
                {t.wordCount}: {wordCount}
              </span>
              {personalIdentifiersProtected !== null && personalIdentifiersProtected !== undefined && !isNaN(Number(personalIdentifiersProtected)) && (
                <span className="font-roboto text-[10px] sm:text-[11px] font-bold text-[#3D2B90] uppercase tracking-wide flex items-center gap-1.5 bg-[#EDE9F8] px-2.5 py-0.5 rounded-full shadow-xs">
                  <Shield className="w-3.5 h-3.5 text-[#3D2B90] flex-shrink-0" />
                  <span>{t.personalIdentifiersProtected || "Protected Identifiers"}:</span>
                  <span className="font-black text-[#3D2B90]">{Number(personalIdentifiersProtected)}</span>
                </span>
              )}
            </div>
            {(transcriptionText || translationChunks.length > 0 || selectedTranslation) && (
              <button
                type="button"
                onClick={handleCopyAll}
                title={copiedAll ? t.copied : t.copy}
                className="flex items-center justify-center w-[34px] h-[34px] rounded-full bg-[#E8E4F8] text-[#3D2B90] hover:bg-[#d4cef0] transition-colors shadow-sm flex-shrink-0 ml-auto"
              >
                {copiedAll ? (
                  <BadgeCheckIcon className="h-4 w-4 text-[#3D2B90]" />
                ) : (
                  <img src={CopyIcon} alt="copy" className="h-4 w-4 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(18%) sepia(60%) saturate(2200%) hue-rotate(240deg) brightness(85%)" }} />
                )}
              </button>
            )}
          </div>
        </div>

      </div>

      {/* Bottom bar — always visible, disabled when no data */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 mt-2 sm:mt-4 flex-shrink-0">
        <div className={`flex items-center gap-2 flex-shrink-0 min-w-0 ${isTokenExhausted || recordingError || (showLangMsg && !selectedLanguage) ? "bg-red-50 border border-red-100 px-2 py-1 rounded-md" : ""}`}>
          {isTokenExhausted || recordingError || (showLangMsg && !selectedLanguage) ? (
            <AlertCircle className="h-[18px] w-[18px] text-red-500 flex-shrink-0" />
          ) : (
            <img src={WarningIcon} alt="warning" className={`h-[18px] w-[18px] object-contain flex-shrink-0 ${!selectedTranslation ? "opacity-40" : ""}`} />
          )}
          {isTokenExhausted ? (
            <span
              className="font-roboto font-bold text-[13px] text-red-500 leading-none tracking-normal truncate cursor-pointer hover:underline"
              onClick={() => navigate("/plans")}
            >
              {t.upgradePlan || "Please upgrade your plan."}
            </span>
          ) : recordingError ? (
            <span className="font-roboto font-bold text-[13px] text-red-500 leading-none tracking-normal truncate">
              {recordingError}
            </span>
          ) : showLangMsg && !selectedLanguage ? (
            <span className="font-roboto font-bold text-[13px] text-red-500 leading-none tracking-normal truncate">
              {t.selectAudioLangFirst || t.selectAudioLangUpload || "Please select an audio language first"}
            </span>
          ) : (
            <span className={`font-roboto font-bold text-[13px] text-[#3D2B90] leading-none tracking-normal truncate ${!selectedTranslation ? "opacity-40" : ""}`}>
              {t.transcribeWarning}
            </span>
          )}
        </div>
        <button
          onClick={handleExport}
          disabled={!selectedTranslation}
          className={`btn-gradient-border w-full sm:w-auto flex items-center justify-center gap-[8.44px] sm:gap-[13.41px] h-[40px] px-[20px] sm:px-[25.32px] font-roboto font-bold text-dark-navy text-xs uppercase tracking-wide whitespace-nowrap flex-shrink-0 transition-all ${selectedTranslation
            ? "cursor-pointer hover:opacity-85"
            : "opacity-40 cursor-not-allowed"
            }`}
        >
          <img
            src={ExportIcon}
            alt="export"
            className="h-3.5 w-3.5 object-contain flex-shrink-0"
            style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }}
          />
          {t.exportBtn}
        </button>
      </div>

      <MenuAIPanel
        isOpen={aiMenuOpen}
        onClose={() => setAiMenuOpen(false)}
        presets={presets}
        originalText={transcriptionText}
        translatedText={transcriptionText}
        mode="transcribe"
        uploadedFile={uploadedFile}
        onResult={(result) => {
          commitCurrentToHistory();

          const rawPi =
            result?.personalIdentifiersProtected ??
            result?.personal_identifiers_protected ??
            result?.data?.personalIdentifiersProtected ??
            result?.data?.personal_identifiers_protected ??
            null;
          if (rawPi !== null && rawPi !== undefined && !isNaN(Number(rawPi))) {
            setPersonalIdentifiersProtected(Number(rawPi));
            personalIdentifiersProtectedRef.current = Number(rawPi);
          }

          const segments = Array.isArray(result?.segments) ? result.segments : [];
          if (segments.length > 0) {
            const chunks = segments.map((s) => ({
              timestamp: s.timestamp || "",
              translatedText: s.translatedText || "",
              person: s.person || "",
              originalText: s.originalText || "",
            }));
            const joined = chunks.map((c) => c.translatedText).filter(Boolean).join(" ");
            const transcription = result.transcription || chunks.map((c) => c.originalText).filter(Boolean).join(" ");
            const lastTs = chunks[chunks.length - 1]?.timestamp || "";

            transcriptionTextRef.current = transcription;
            savedTranscriptionRef.current = transcription;
            savedTranslationRef.current = joined;
            currentChunksRef.current = chunks;
            lastTimestampRef.current = lastTs;
            setTranscriptionText(transcription);
            setSelectedTranslation(joined);
            setTranslationChunks(chunks);
            setLastTimestamp(lastTs);
            setWordCount(joined.trim() === "" ? 0 : joined.trim().split(/\s+/).length);
            setIsTextDirty(false);
            setIsCurrentTranscriptionSaved(true);
            commitCurrentToHistory(true);
            return;
          }

          const improved = typeof result === "string"
            ? result
            : (result?.translation || result?.translatedText || result?.originalText || "");
          if (!improved) return;

          const transcription = typeof result === "object" ? (result.transcription || result.originalText || "") : "";

          setTranslationChunks([]);
          currentChunksRef.current = [];
          savedTranslationRef.current = improved;
          setSelectedTranslation(improved);
          setWordCount(improved.trim() === "" ? 0 : improved.trim().split(/\s+/).length);
          if (transcription) {
            transcriptionTextRef.current = transcription;
            savedTranscriptionRef.current = transcription;
            setTranscriptionText(transcription);
          }
          setIsTextDirty(false);
          setIsCurrentTranscriptionSaved(true);
          persistAll();
          commitCurrentToHistory(true);
        }}
      />

      {/* Export Format Picker */}
      {showExportPicker && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setShowExportPicker(false)}
          onKeyDown={(e) => { if (e.key === "Escape") setShowExportPicker(false); }}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <div className="relative bg-white rounded-[27px] p-6 w-full max-w-sm flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowExportPicker(false)}
              className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center text-[#3D2B90] hover:opacity-70 transition-opacity"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L15 15M15 1L1 15" stroke="#3D2B90" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">{t.downloadAs}</h2>
            <div className="flex gap-2.5 sm:gap-3">
              <button
                type="button"
                onClick={() => doExport("text")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.text}
              </button>
              <button
                type="button"
                onClick={() => doExport("pdf")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.pdf}
              </button>
              <button
                type="button"
                onClick={() => doExport("word")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.word || "WORD"}
              </button>
            </div>

            <div className="flex items-center my-0.5">
              <div className="flex-1 border-t border-[#E2DCF0]"></div>
              <span className="px-3 text-[11px] font-bold text-[#9190A4] uppercase tracking-wider">{t.or || "OR"}</span>
              <div className="flex-1 border-t border-[#E2DCF0]"></div>
            </div>

            <button
              type="button"
              onClick={handleSendForTranslation}
              className="w-full flex items-center justify-center gap-2.5 h-[44px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-bold text-xs uppercase leading-none px-4 hover:bg-[#F3F1F9] transition-colors cursor-pointer"
            >
              <Languages className="w-5 h-5 text-[#3D2B90] flex-shrink-0" />
              <span>{t.sendToTranslation || "SEND TO TRANSLATION"}</span>
            </button>
          </div>
        </div>
      )}

      {/* New Transcribe Confirmation Modal */}
      {showNewTranscribeConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4" onClick={() => setShowNewTranscribeConfirm(false)}>
          <div className="bg-white rounded-[27px] p-6 w-full max-w-md flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">
              {t.newTranscribeSessionTitle}
            </h2>
            <p className="text-[12px] text-[#9190A4] text-center font-roboto">
              {t.newTranscribeConfirm}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowNewTranscribeConfirm(false)}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.newSessionCancel}
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowNewTranscribeConfirm(false);
                  if (confirmActionRef.current === "record" && !selectedLanguage) {
                    setShowLangMsg(true);
                    setRecordingError(t.selectAudioLangFirst || "Please select an audio language first");
                    return;
                  }
                  const savedLang = selectedLanguageRef.current || selectedLanguage;
                  try { await HistoryService.deleteAudioTranslations(); } catch (_e) { }
                  try { localStorage.removeItem(LS_KEY); } catch (_e) { }
                  idbClearFile().catch(() => { });
                  handleReset(true);
                  if (savedLang) {
                    setSelectedLanguage(savedLang);
                    selectedLanguageRef.current = savedLang;
                  }
                  if (confirmActionRef.current === "record") {
                    handleRecord();
                  } else {
                    setTimeout(() => { audioFileRef.current?.click(); }, 100);
                  }
                }}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#2e1f72] transition-colors"
              >
                {t.newSessionConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
      {showAlreadySentPopup && (
        <AlreadySentPopup
          t={t}
          onClose={() => setShowAlreadySentPopup(false)}
        />
      )}
      {showContactUs && (
        <ContactUsPopup
          t={t}
          onClose={() => setShowContactUs(false)}
          onSubmit={handleContactUsSubmit}
          isSubmitting={isContactUsSubmitting}
          isSuccess={false}
          prefilledEmail={readStoredEmail() || ""}
        />
      )}
      {contactUsSuccess && (
        <ContactUsPopup
          t={t}
          onClose={() => setContactUsSuccess(false)}
          onSubmit={() => {}}
          isSubmitting={false}
          isSuccess={true}
        />
      )}
    </>
  );
};

export default TranscribeTab;